/**
 * Billing Dashboard Component - Premium UI Redesign
 * Modern SaaS billing interface with aggressive styling
 * 
 * Features:
 * - Unified action bar with primary CTA
 * - Compact KPI cards with strong visual hierarchy
 * - Single-row filter control panel
 * - Enhanced table with zebra rows, aligned currency
 * - Consistent status badges with modern styling
 * - Global Column Sorting via useTableSort hook
 * 
 * Access: Accounting and Admin roles only
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Calendar,
  CalendarRange,
  Layers,
  Building2,
  CheckCircle2,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Receipt,
  CreditCard,
  Eye,
  Mail,
  ArrowLeft,
  Sparkles,
  Filter,
  X,
  Download,
  Upload,
  TrendingUp,
  DollarSign,
  BarChart3,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  Check,
  Loader2,
  LogOut,
  User as UserIcon,
  FileSpreadsheet,
  ArrowRight,
} from 'lucide-react';
import './billing/billing-premium.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent } from '../ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { formatStreetAddress } from '../../utils/textFormat';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarPicker } from '../ui/calendar';
import { useAuth } from '../../App';
import useTableSort, { sortData } from '../../hooks/useTableSort';

// Extracted Dialog Components
import { InvoiceDetailsDialog } from './billing/InvoiceDetailsDialog';
import { ExcludedOrdersTable } from './billing/ExcludedOrdersTable';
import { SendInvoiceModal } from './billing/SendInvoiceModal';
import AuditReviewPanel from './billing/AuditReviewPanel';
import QcGatedQueuePanel from './billing/QcGatedQueuePanel';
import { InvoicePaymentModal } from './billing/InvoicePaymentModal';
import { RecordPaymentModal } from './billing/RecordPaymentModal';
import { BulkPaymentUploadModal } from './billing/BulkPaymentUploadModal';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Premium Status Badge Component
const StatusBadge = ({ status }) => {
  const configs = {
    paid: {
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-600',
      border: 'border-emerald-500/20',
      dot: 'bg-emerald-500',
      label: 'Paid'
    },
    partially_paid: {
      bg: 'bg-amber-500/10',
      text: 'text-amber-700',
      border: 'border-amber-500/30',
      dot: 'bg-amber-500',
      label: 'Partially Paid'
    },
    invoiced: {
      bg: 'bg-blue-500/10',
      text: 'text-blue-600',
      border: 'border-blue-500/20',
      dot: 'bg-blue-500',
      label: 'Invoiced'
    },
    unbilled: {
      bg: 'bg-amber-500/10',
      text: 'text-amber-600',
      border: 'border-amber-500/20',
      dot: 'bg-amber-500',
      label: 'Unbilled'
    }
  };
  
  const config = configs[status] || configs.unbilled;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

// Margin Badge for Profit View
const MarginBadge = ({ margin }) => {
  if (margin === null || margin === undefined || isNaN(margin)) {
    return <span className="text-xs text-slate-400">N/A</span>;
  }
  const isHealthy = margin > 40;
  const isMedium = margin >= 20 && margin <= 40;
  const isLow = margin < 20;
  
  return (
    <span
      data-testid="margin-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
        isHealthy
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : isMedium
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-red-50 text-red-700 border-red-200'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isHealthy ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-red-500'}`} />
      {margin.toFixed(1)}%
    </span>
  );
};

// Audit Required Badge
const AuditBadge = ({ status, reasons }) => {
  if (status === 'completed') {
    return (
      <span
        data-testid="audit-completed-badge"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white cursor-default"
      >
        <CheckCircle2 className="w-3 h-3" />
        Audited
      </span>
    );
  }
  
  if (!reasons || reasons.length === 0) return null;
  
  return (
    <span
      data-testid="audit-required-badge"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white cursor-default"
    >
      <AlertTriangle className="w-3 h-3" />
      Audit
    </span>
  );
};

// Charge Breakdown Tooltip Content
const ChargeBreakdownContent = ({ order, formatCurrency }) => {
  // May 2, 2026 — Per-parcel billing.
  // `unit_rate` is the per-parcel rate; `parcel_count` is the qty.
  // Fallback to legacy single-parcel display when the order doesn't
  // have parcel metadata yet (e.g., Title Search products).
  const unitRate = order.billing_override_price ?? order.billing_unit_price_snapshot ?? 0;
  const parcelCount = Math.max(parseInt(order.parcel_count || order.billing_quantity || 1, 10), 1);
  const basePrice = unitRate * parcelCount;
  const breakdown = order.charge_breakdown || {};
  const hasCharges = Object.keys(breakdown).length > 0;

  return (
    <div className="text-xs space-y-2 min-w-[220px]">
      <div className="flex justify-between items-center pb-1.5 border-b border-slate-200">
        <span className="text-slate-500">
          Unit Rate {parcelCount > 1 ? `× ${parcelCount}` : ''}
        </span>
        <span className="font-semibold text-slate-800">
          {parcelCount > 1
            ? `${formatCurrency(unitRate)} × ${parcelCount} = ${formatCurrency(basePrice)}`
            : formatCurrency(unitRate)}
        </span>
      </div>
      {parcelCount > 1 && (
        <div className="flex justify-between items-center text-[11px]">
          <span className="text-slate-500">Parcels</span>
          <span className="font-medium text-blue-700">{parcelCount} Parcels</span>
        </div>
      )}
      {hasCharges && Object.entries(breakdown).map(([type, data]) => (
        <div key={type}>
          <div className="flex justify-between items-center">
            <span className="text-slate-500">{type} {data.count > 1 ? `(x${data.count})` : ''}</span>
            <span className="font-medium text-slate-700">{formatCurrency(data.total)}</span>
          </div>
          {data.items.map((item, i) => (
            <p key={i} className="text-[10px] text-slate-400 ml-2 truncate max-w-[180px]">
              {item.description || `${type}`} — {formatCurrency(item.amount)}
            </p>
          ))}
        </div>
      ))}
      <div className="flex justify-between items-center pt-1.5 border-t border-slate-300 font-bold">
        <span className="text-slate-700">Total Billable</span>
        <span className="text-emerald-700">{formatCurrency(order.total_billable_amount || basePrice)}</span>
      </div>
    </div>
  );
};

// KPI Card — TitleFlow finance-grade card (Apr 25, 2026 standardization)
//
// Visual contract:
//   • Default: white surface, neutral border, soft shadow, neutral icon chip.
//   • Active: blue top accent strip (#4F46E5), faint indigo tint (#EEF2FF),
//     ring + slightly elevated shadow, indigo icon chip.
//   • Hover: subtle border + shadow lift, never blue (color reserved for active).
//
// Cards are click-to-filter; the parent owns an `activeKpi` flag so the
// active highlight is unambiguous (was previously sharing the same condition
// across two cards — bug fixed Apr 25, 2026).
/* BILLING-UI-PREMIUM-001 — reduced-motion aware count-up (rAF, ~650ms ease-out). */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useCountUp = (target, { duration = 650 } = {}) => {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const to = Number.isFinite(target) ? target : 0;
    if (prefersReducedMotion()) { setDisplay(to); fromRef.current = to; return undefined; }
    const from = fromRef.current;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return display;
};

/* Tiny inline sparkline — REAL data only (caller passes monthly series or nothing). */
const Sparkline = ({ points, color }) => {
  if (!points || points.length < 3) return null;
  const w = 72, h = 22, pad = 2;
  const max = Math.max(...points), min = Math.min(...points);
  const span = max - min || 1;
  const xs = points.map((v, i) => [
    pad + (i * (w - pad * 2)) / (points.length - 1),
    h - pad - ((v - min) / span) * (h - pad * 2),
  ]);
  const d = xs.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${d} L${xs[xs.length - 1][0].toFixed(1)},${h} L${xs[0][0].toFixed(1)},${h} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden="true" data-testid="kpi-sparkline">
      <path d={area} fill={color} opacity="0.12" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/* BILLING-UI-PREMIUM-003 — decorative mini bars. Data-driven when a real trend exists (same series the
   sparkline uses); otherwise a static, purely ornamental pattern (aria-hidden, low opacity, no axis). */
const DECOR_BARS = [34, 52, 44, 66, 58, 80, 72];
const KpiBars = ({ points, color, active }) => {
  const real = Array.isArray(points) && points.length >= 3;
  let heights = DECOR_BARS;
  if (real) {
    const src = points.slice(-7);
    const max = Math.max(...src, 1);
    heights = src.map((v) => Math.max(14, Math.round((v / max) * 80)));
  }
  return (
    <span className="bd-bars shrink-0" aria-hidden="true" data-decorative={real ? 'false' : 'true'}>
      {heights.map((h, i) => (
        <i key={i} style={{ height: `${h}%`, animationDelay: `${i * 60}ms`, background: active ? 'rgba(255,255,255,0.9)' : color, opacity: real ? 0.9 : (active ? 0.55 : 0.32) }} />
      ))}
    </span>
  );
};


const KPICard = ({ title, subtitle, value, numericValue, format, helper, icon: Icon, colorConfig, onClick, active, trend, enterIndex = 0, status = null, onRetry }) => {
  const animated = useCountUp(Number.isFinite(numericValue) ? numericValue : 0);
  const shown = Number.isFinite(numericValue) && typeof format === 'function' ? format(animated) : value;
  /* BILLING-UI-PREMIUM-001 (Nov 2026) — compact card (~84px): active = navy→indigo gradient, white type. */
  return (
    <div
      onClick={onClick}
      className={`
        bd-card bd-kpi bd-enter bd-enter-${Math.min(enterIndex + 1, 5)} group relative rounded-2xl border px-4 py-3 min-h-[84px]
        cursor-pointer select-none overflow-hidden
        ${active
          ? 'bd-kpi-active border-transparent text-white shadow-[0_14px_30px_-14px_rgba(49,46,129,0.75)]'
          : 'border-[#E5E7EB] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_24px_-20px_rgba(15,23,42,0.25)] hover:shadow-[0_14px_30px_-18px_rgba(15,23,42,0.3)] hover:border-[#CBD5E1]'
        }
      `}
      style={active ? undefined : { background: `linear-gradient(135deg, ${colorConfig.tint} 0%, #FFFFFF 48%, ${colorConfig.tint}80 100%)` }}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      data-status={status || 'ready'}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <div className="flex items-center gap-3">
        <div className={`flex items-center justify-center rounded-xl flex-shrink-0 ${active ? 'bg-white/15 ring-1 ring-white/25' : 'shadow-sm'}`}
          style={{ width: 38, height: 38, backgroundColor: active ? undefined : colorConfig.iconBg }}>
          <Icon className="w-[18px] h-[18px]" style={{ color: active ? '#FFFFFF' : colorConfig.iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-[12px] font-semibold leading-4 truncate ${active ? 'text-white/85' : 'text-[#4B5563]'}`}>{title}</p>
            <span className="flex items-center gap-1 shrink-0">
              {status === 'refreshing' && (
                <Loader2 className={`w-3.5 h-3.5 animate-spin ${active ? 'text-white/70' : 'text-slate-400'}`} data-testid="kpi-refreshing" aria-label="Refreshing" />
              )}
              {status === 'error' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRetry?.(); }}
                  className="flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100"
                  title="This value could not be refreshed — showing the last known value. Click to retry."
                  data-testid="kpi-retry"
                >
                  <AlertTriangle className="w-3 h-3" /> Retry
                </button>
              )}
              <span className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-200 ${active ? 'bg-white/20 group-hover:bg-white/30' : 'bg-slate-100 group-hover:bg-white'}`}>
                <ChevronRight className={`w-3.5 h-3.5 ${active ? 'text-white' : 'text-slate-500'}`} />
              </span>
            </span>
          </div>
          <div className="mt-0.5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-[22px] font-bold leading-7 tracking-tight tabular-nums truncate transition-opacity duration-200 ${active ? 'text-white' : 'text-[#111827]'} ${status === 'refreshing' ? 'opacity-60' : 'opacity-100'}`} data-testid="kpi-value">{shown}</p>
              {(subtitle || helper) && (
                <p className={`text-[11px] font-medium leading-4 ${active ? 'text-white/75' : 'text-[#6B7280]'}`}>
                  {subtitle && <span className="block truncate">{subtitle}</span>}
                  {helper && <span className="block truncate">{helper}</span>}
                </p>
              )}
            </div>
            {trend && trend.length >= 3 ? <Sparkline points={trend} color={active ? '#FFFFFF' : colorConfig.iconColor} /> : <KpiBars color={colorConfig.iconColor} active={active} />}
          </div>
        </div>
      </div>
    </div>
  );
};


const CountRetryButton = ({ onClick, refreshing, testid, tone }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    className={`bd-chip h-8 px-3 text-xs font-semibold shrink-0 rounded-lg border flex items-center gap-1 bg-white text-${tone}-800 border-${tone}-200 hover:bg-${tone}-50`}
    data-testid={testid}
    title="This count could not be refreshed. Click to retry."
  >
    {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Retry
  </button>
);

const PanelError = ({ message, onRetry, testid }) => (
  <div className="mx-6 my-3 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid={testid} role="alert">
    <span className="flex items-center gap-2 text-sm text-amber-900"><AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />{message}</span>
    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onRetry} data-testid={`${testid}-retry`}>
      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
    </Button>
  </div>
);

/* Feb 2026 — Stable date-range field replacing the buggy native
   <input type="date">. Architecture:
     * Trigger button shows the formatted current value.
     * Click opens a Popover containing:
         - A small text input for fast YYYY-MM-DD typing (commits ONLY
           on Enter / blur, never on partial keystrokes).
         - A Calendar component for visual selection (commits on day
           click, then closes the popover).
   Crucially, NO state — neither local draft nor canonical — escapes
   this component until commit, so the parent's filter useEffect will
   not refetch mid-typing. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const formatDisplayDate = (iso) => {
  if (!iso || !ISO_DATE_RE.test(iso)) return '';
  // "2026-04-15" -> "Apr 15, 2026"  (no time-zone surprises — purely string ops + Date)
  return formatDateOnly(iso);
};

const DateRangeField = ({ label, value, onCommit, testid, disabled = false, disabledReason }) => {
  const [open, setOpen] = React.useState(false);
  // Local typed-text state. Initialized from props on each open.
  const [draft, setDraft] = React.useState(value || '');
  
  // Re-sync draft from props when popover opens, OR when external
  // commits change `value` (e.g. preset chips, "This Month"). NOT
  // while the popover is open with the user typing.
  React.useEffect(() => {
    if (!open) setDraft(value || '');
  }, [value, open]);
  
  const commitIfValid = (next) => {
    if (next === '' || ISO_DATE_RE.test(next)) {
      if (next !== value) onCommit(next);
      setOpen(false);
    }
  };
  
  const onCalendarSelect = (date) => {
    if (!date) {
      commitIfValid('');
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    commitIfValid(`${yyyy}-${mm}-${dd}`);
  };
  
  // Selected date for the Calendar component (visual highlight + initial month)
  const selectedDate = ISO_DATE_RE.test(value)
    ? (() => { const [y, m, d] = value.split('-').map(Number); return new Date(y, m - 1, d); })()
    : undefined;
  
  return (
    <Popover open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
          aria-disabled={disabled}
          className={`
            relative h-9 w-[140px] pl-8 pr-2 text-sm border border-slate-200 rounded-md
            bg-white text-left
            ${value ? 'text-slate-900' : 'text-slate-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'hover:border-slate-300'}
            focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400
            transition-colors
          `}
          data-testid={testid}
          data-disabled={disabled ? 'true' : 'false'}
          aria-label={`${label} date`}
        >
          <Calendar className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <span className="block truncate">
            {value ? formatDisplayDate(value) : `${label}…`}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
        {/* Manual-entry strip — typed values stay in `draft` until
            Enter or blur. */}
        <div className="px-3 pt-3 pb-2 border-b border-slate-100">
          <Input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitIfValid(draft.trim()); }
              if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
            }}
            placeholder="YYYY-MM-DD"
            className="h-8 text-sm font-mono"
            data-testid={`${testid}-text-input`}
            autoFocus
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-slate-400">Press Enter to apply</span>
            {value && (
              <button
                type="button"
                onClick={() => commitIfValid('')}
                className="text-[10px] text-slate-500 hover:text-red-600"
                data-testid={`${testid}-clear-btn`}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <CalendarPicker
          mode="single"
          selected={selectedDate}
          onSelect={onCalendarSelect}
          defaultMonth={selectedDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};


/* Phase B UI (Feb 2026) — Monthly summary view.
   Compact two-row layout: a horizontal-bar chart for Invoiced vs Paid by
   month (Recharts) on top, and a finance-grade table below. Source of
   truth is the `payments` collection (Phase C), surfaced via
   GET /pricing/billing/monthly-summary. */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-04" -> "Apr 2026" — Phase D label formatter. */
const formatMonthLabel = (yyyymm) => {
  if (!yyyymm || typeof yyyymm !== 'string') return yyyymm || '';
  const [y, m] = yyyymm.split('-');
  const idx = parseInt(m, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > 11) return yyyymm;
  return `${MONTH_NAMES[idx]} ${y}`;
};

const MonthlyView = ({ data, isLoading, formatCurrency }) => {
  const months = (data?.months || []).slice().sort((a, b) =>
    a.billing_month.localeCompare(b.billing_month)
  );
  const totals = data?.totals;
  
  const chartData = months.map((m) => ({
    name: formatMonthLabel(m.billing_month),
    Invoiced: m.invoiced_amount,
    Paid: m.paid_amount,
  }));
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center" data-testid="monthly-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        <p className="text-xs text-slate-500 mt-2">Loading monthly summary…</p>
      </div>
    );
  }
  
  if (!months.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center" data-testid="monthly-empty">
        <p className="text-sm text-slate-500">No billing data available for the selected filters.</p>
        <p className="text-xs text-slate-400 mt-1">Try widening the date range or clearing the client filter.</p>
      </div>
    );
  }
  
  const collectionRate = totals && totals.invoiced_amount > 0
    ? (totals.paid_amount / totals.invoiced_amount) * 100
    : 0;
  const isSingleMonth = months.length === 1;
  
  return (
    <div className="space-y-4" data-testid="monthly-view">
      {/* Headline totals */}
      {totals && (
        <div className="bg-white rounded-xl border border-slate-200/60 px-4 py-3 grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Total Invoiced</p>
            <p className="text-base font-bold text-slate-900 tabular-nums">{formatCurrency(totals.invoiced_amount)}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Total Paid</p>
            <p className="text-base font-bold text-emerald-700 tabular-nums">{formatCurrency(totals.paid_amount)}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Outstanding</p>
            <p className="text-base font-bold text-amber-700 tabular-nums">{formatCurrency(totals.balance_amount)}</p>
          </div>
          <div data-testid="kpi-collection-rate">
            <p className="text-[11px] text-slate-500 font-medium">Collection Rate</p>
            <p className="text-base font-bold text-indigo-700 tabular-nums">{collectionRate.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Invoices</p>
            <p className="text-base font-bold text-slate-900 tabular-nums">{totals.invoice_count}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Payments</p>
            <p className="text-base font-bold text-slate-900 tabular-nums">{totals.payment_count}</p>
          </div>
        </div>
      )}
      
      {/* Chart — Invoiced vs Paid by month */}
      <div className="bg-white rounded-xl border border-slate-200/60 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Invoiced vs Paid</h3>
          <span className="text-[11px] text-slate-400">
            {isSingleMonth ? 'Showing last 1 month of data' : 'By billing month'}
          </span>
        </div>
        <div style={{ width: '100%', height: 220 }} data-testid="monthly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
              barCategoryGap={isSingleMonth ? '40%' : '20%'}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
              <YAxis
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)}
              />
              <RTooltip
                cursor={{ fill: 'rgba(79, 70, 229, 0.04)' }}
                contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E5E7EB' }}
                formatter={(v) => formatCurrency(v)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Invoiced" fill="#4F46E5" radius={[3, 3, 0, 0]} maxBarSize={isSingleMonth ? 80 : 48} />
              <Bar dataKey="Paid" fill="#10B981" radius={[3, 3, 0, 0]} maxBarSize={isSingleMonth ? 80 : 48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Compact Monthly Table */}
      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Month-on-Month Breakdown</h3>
          <span className="text-[11px] text-slate-400">{months.length} {months.length === 1 ? 'month' : 'months'}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-table">
            <thead className="tf-thead border-b border-slate-200/70">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Month</th>
                <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Invoiced</th>
                <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Paid</th>
                <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Outstanding</th>
                <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Invoices</th>
                <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Payments</th>
              </tr>
            </thead>
            <tbody>
              {months.slice().reverse().map((m) => {
                const collectionRate = m.invoiced_amount > 0
                  ? Math.round((m.paid_amount / m.invoiced_amount) * 100)
                  : 0;
                return (
                  <tr key={m.billing_month} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`monthly-row-${m.billing_month}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      {formatMonthLabel(m.billing_month)}
                      <span className="ml-2 text-[10px] text-slate-400 font-normal">{collectionRate}% collected</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{formatCurrency(m.invoiced_amount)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{formatCurrency(m.paid_amount)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{formatCurrency(m.balance_amount)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{m.invoice_count}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{m.payment_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};



// BILLING-FILTER-DRAFT-001 (Jun 2026) — default filter values shared by
// the applied state initialisers and Clear Filters (draft reset).
const defaultDateFrom = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split('T')[0];
};
const defaultDateTo = () => new Date().toISOString().split('T')[0];
const EMPTY_ROWS = [];
const UNBILLED_DATES_NOTE = 'Unbilled shows all outstanding eligible orders — the date range does not apply.';
const EXCLUDED_DATES_NOTE = 'Excluded shows all currently excluded orders — the date range does not apply.';

export const BillingDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const hasAccess = user?.role === 'admin' || user?.role === 'accounting';
  const isAccountingOnly = user?.role === 'accounting';
  
  // State
  const [activeTab, setActiveTab] = useState('orders');
  const [billingView, setBillingView] = useState('external');
  const [clientCodes, setClientCodes] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [billingStatusFilter, setBillingStatusFilter] = useState('unbilled');
  // BILLING-FILTER-DRAFT-001 (Jun 2026) — Draft vs Applied filter model.
  // The inputs (Client / From / To / Status) edit DRAFT state only; the
  // dashboard keeps showing results for the APPLIED state until the user
  // clicks "Apply Filters", which copies draft → applied and triggers ONE
  // combined request. No calculation/eligibility logic touched.
  const [draftClient, setDraftClient] = useState('all');
  const [draftDateFrom, setDraftDateFrom] = useState(defaultDateFrom);
  const [draftDateTo, setDraftDateTo] = useState(defaultDateTo);
  // BILLING-UNBILLED-FILTER-002 (Nov 2026) — optional period on the Unbilled tab.
  // 'all' (default) = whole backlog (unchanged); 'month' | 'custom' → server `period_scope=period`.
  const [unbilledScope, setUnbilledScope] = useState('all');
  const [draftUnbilledScope, setDraftUnbilledScope] = useState('all');
  const [draftUnbilledMonth, setDraftUnbilledMonth] = useState(() => new Date().toISOString().slice(0, 7));
  // BILLING-STATUS-TABS-001 (Sep 2026): status pills are immediate view tabs — only
  // Client / Date remain under the Apply Filters (draft) model.
  // BILLING-UNBILLED-BACKLOG-001: under Unbilled the date range does not apply (server ignores it),
  // so date drafts never make the filters "dirty" there; they are kept for Invoiced / Paid / All.
  const isOutstandingQueue = billingStatusFilter === 'unbilled' || billingStatusFilter === 'excluded';
  const isExcludedQueue = billingStatusFilter === 'excluded';
  const unbilledPeriodActive = billingStatusFilter === 'unbilled' && unbilledScope !== 'all';
  const draftUnbilledPeriod = billingStatusFilter === 'unbilled' && draftUnbilledScope !== 'all';
  const datesInactive = isExcludedQueue || (billingStatusFilter === 'unbilled' && !draftUnbilledPeriod);
  const outstandingDatesNote = isExcludedQueue ? EXCLUDED_DATES_NOTE : UNBILLED_DATES_NOTE;
  // BILLING-UNBILLED-INVOICE-COL-001: Invoice column only where invoice info exists (All tab).
  const showInvoiceColumn = billingView === 'external' && !isOutstandingQueue;
  const ordersColCount = billingView === 'profit' ? 8 : billingView === 'internal' ? 7 : (showInvoiceColumn ? 11 : 10);
  const filtersDirty = draftClient !== selectedClient
    || (billingStatusFilter === 'unbilled' && draftUnbilledScope !== unbilledScope)
    || ((!isOutstandingQueue || draftUnbilledPeriod) && (draftDateFrom !== dateFrom || draftDateTo !== dateTo));
  // Apr 25, 2026 — track which KPI card is currently the active selection.
  // Was previously inferred from (activeTab + billingStatusFilter), which
  // caused both "Total Unbilled Amount" and "Orders Ready for Billing" to
  // light up together because they share the same underlying filter.
  const [activeKpi, setActiveKpi] = useState('unbilled-amount');

  /**
   * BILLING-SYNC-001 (Feb 2026) — keep `activeTab` strictly in sync with
   * `billingStatusFilter`.
   *
   * Defect symptom (Issue #1): opening the Billing page with "Unbilled"
   * selected showed the KPIs correctly (Total Unbilled $696 / 200 orders)
   * but rendered the empty "Generated Invoices" table instead of the
   * unbilled orders. Clicking Invoiced → Unbilled forced a re-sync and
   * the orders table finally appeared.
   *
   * Root cause: `activeTab` and `billingStatusFilter` were independent
   * useState variables. handleGenerateInvoice (line 1212) called
   * setActiveTab('invoices') WITHOUT updating billingStatusFilter, leaving
   * the two out of sync for the rest of the session. The status-filter
   * pill labelled the page "Unbilled" while <Tabs value="invoices">
   * rendered the wrong <TabsContent>.
   *
   * Fix: derive `activeTab` from `billingStatusFilter`. Any call site
   * that previously did setActiveTab + setBillingStatusFilter now only
   * needs to set the status filter; activeTab follows automatically.
   *
   * Backwards-compat: stale `setActiveTab` callers still work but their
   * write is reactively overwritten on the next render if it disagrees
   * with `billingStatusFilter` — eliminating the desync class entirely.
   */
  useEffect(() => {
    const expected = (billingStatusFilter === 'invoiced' || billingStatusFilter === 'paid')
      ? 'invoices'
      : 'orders';
    if (activeTab !== expected) {
      setActiveTab(expected);
    }
  }, [billingStatusFilter, activeTab]);

  // BILLING-FILTER-DRAFT-001 — when the APPLIED filters change through a
  // programmatic path (KPI card click, invoice generation, Apply), mirror
  // them into the draft inputs so UI selection always matches the data.
  useEffect(() => {
    setDraftClient(selectedClient);
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
  }, [selectedClient, dateFrom, dateTo]);

  // PERF-BILLING-001: exception counters (missing pricing / QC-gated / delivery exceptions)
  // are informational — they start only after the main table has rendered once so they
  // never compete with Apply Filters for the database.
  const [tableLoadedOnce, setTableLoadedOnce] = useState(false);
  const countsReady = hasAccess && tableLoadedOnce;

  const handleApplyFilters = () => {
    if (!filtersDirty) return;
    // One React batch → applied deps change together → single fetch.
    setSelectedOrderIds(new Set());
    setCurrentPage(1);
    setSelectedClient(draftClient);
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setUnbilledScope(draftUnbilledScope);
  };

  // BILLING-UNBILLED-FILTER-002 — scope switch (draft). Month → From/To = 1st…last day of that month.
  const monthBounds = (ym) => {
    const [y, m] = (ym || '').split('-').map(Number);
    if (!y || !m) return null;
    const last = new Date(y, m, 0).getDate();
    return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
  };
  const handleUnbilledScope = (scope) => {
    setDraftUnbilledScope(scope);
    if (scope === 'month') {
      const b = monthBounds(draftUnbilledMonth);
      if (b) { setDraftDateFrom(b.from); setDraftDateTo(b.to); }
    }
  };
  const handleUnbilledMonth = (ym) => {
    setDraftUnbilledMonth(ym);
    const b = monthBounds(ym);
    if (b) { setDraftDateFrom(b.from); setDraftDateTo(b.to); }
  };
  const clearUnbilledPeriod = () => {
    setDraftUnbilledScope('all');
    setUnbilledScope('all');
    setSelectedOrderIds(new Set());
    setCurrentPage(1);
  };
  const unbilledPeriodLabel = (() => {
    if (!unbilledPeriodActive) return null;
    if (unbilledScope === 'month' && dateFrom) {
      return new Date(`${dateFrom}T00:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    }
    const f = (v) => (v ? formatDateOnly(v) : '…');
    return `${f(dateFrom)} – ${f(dateTo)}`;
  })();

  // BILLING-STATUS-TABS-001 — immediate status switch (mirrors the KPI-card path):
  // applied status + KPI highlight + tab, reset page & row selection. Applied
  // Client/Date stay as they are, so the fetch effect re-queries with them.
  const handleStatusTab = (opt) => {
    if (opt.value === billingStatusFilter) return;
    setSelectedOrderIds(new Set());
    setSelectedInvoiceIds(new Set());
    setCurrentPage(1);
    setActiveTab(opt.tab);
    setBillingStatusFilter(opt.value);
    setActiveKpi(opt.kpi);
  };

  // BILLING-CLEAR-001 — Clear = complete reset of the Billing view (draft + applied filters,
  // search, status tab, KPI highlight, page, selections) applied immediately; the fetch effect
  // issues ONE request with default params. Already at defaults → plain refresh.
  const handleClearFilters = () => {
    const dFrom = defaultDateFrom();
    const dTo = defaultDateTo();
    const appliedAtDefaults = selectedClient === 'all' && dateFrom === dFrom && dateTo === dTo
      && billingStatusFilter === 'unbilled' && currentPage === 1 && !debouncedSearch && unbilledScope === 'all';
    setDraftClient('all');
    setDraftDateFrom(dFrom);
    setDraftDateTo(dTo);
    setDraftUnbilledScope('all');
    setUnbilledScope('all');
    setSelectedClient('all');
    setDateFrom(dFrom);
    setDateTo(dTo);
    setSearchQuery('');
    setDebouncedSearch('');
    setBillingStatusFilter('unbilled');
    setActiveTab('orders');
    setActiveKpi('unbilled-amount');
    setCurrentPage(1);
    setSelectedOrderIds(new Set());
    setSelectedInvoiceIds(new Set());
    if (appliedAtDefaults) fetchBillingData(); // nothing for the effect to react to → plain refresh
  };
  // Delivery Exceptions — Feb 25, 2026 P0
  // BILLING-PERF-003 Phase 2: the three exception counters live in ONE state object with
  // per-count {value, error, refreshing}. `value === null` means UNKNOWN (never rendered
  // as 0); the last known value is kept when a refresh fails.
  const [exceptionCounts, setExceptionCounts] = useState({
    delivery: { value: null, error: null, refreshing: false },
    qc: { value: null, error: null, refreshing: false },
    missing: { value: null, error: null, refreshing: false },
    unclassified: { value: null, error: null, refreshing: false },
  });
  const deliveryExceptionCount = exceptionCounts.delivery.value ?? 0;
  const qcGatedCount = exceptionCounts.qc.value ?? { total: 0, held: 0, dollars: 0 };
  const missingPricingCount = exceptionCounts.missing.value ?? 0;
  const setMissingPricingCount = (n) => setExceptionCounts((p) => ({ ...p, missing: { value: n, error: null, refreshing: false } }));
  const [deliveryExceptions, setDeliveryExceptions] = useState([]);
  const [deliveryExceptionsLoading, setDeliveryExceptionsLoading] = useState(false);

  // Missing Pricing — Feb 2026 (Billing Governance Visibility).
  // Surfaces $0.00 orders (no pricing rule + no override) so admins can
  // configure pricing. Strictly blocked from invoice generation by the
  // backend `_billable_status_filter` strict path.
  const [missingPricingOrders, setMissingPricingOrders] = useState([]);
  const [missingPricingLoading, setMissingPricingLoading] = useState(false);
  const [isRecalculatingAll, setIsRecalculatingAll] = useState(false);

  // Deep-link from notification — Feb 25, 2026.
  // /admin/billing?tab=delivery-exceptions&from=notification opens the
  // Delivery Exceptions card view directly. Idempotent — runs once per
  // location.search change.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'delivery-exceptions') {
      setActiveKpi('delivery-exceptions');
    }
  }, [location.search]);
  
  // Data state
  const [billableOrders, setBillableOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    readyCount: null,
    invoicedCount: null,
    paidCount: null,
    readyAmount: null,
    invoicedAmount: null,
    paidAmount: null
  });
  // BILLING-PERF-003 Phase 2: KPI error = last refresh failed/partial → cards keep the
  // last known values and show Retry. `null` KPI values render as "—", never $0.00.
  const [kpiError, setKpiError] = useState(null);
  const [ordersError, setOrdersError] = useState(null);
  const [invoicesError, setInvoicesError] = useState(null);
  const kpiValue = (v, f) => (v == null ? '—' : f(v));
  const kpiNumeric = (v) => (v == null ? NaN : Number(v));
  const [isLoading, setIsLoading] = useState(false);
  // BILLING-FILTER-DRAFT-001 — monotonically increasing request sequence;
  // a slower, older response must never overwrite a newer applied filter.
  const fetchSeqRef = useRef(0);
  // BILLING-PERF-003 Phase 2: in-flight requests are ABORTED (not just ignored) when a
  // newer fetch starts, so stale queries stop consuming the API/DB.
  const fetchAbortRef = useRef(null);
  const isLoadingRef = useRef(false);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  
  // Dialog states
  const [showInvoiceDetailsDialog, setShowInvoiceDetailsDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Phase B UI (Feb 2026) — Monthly view
  // (Aging Buckets strip removed Jun 2026 — BILLING-FILTER-DRAFT-001;
  //  underlying /pricing/billing/aging endpoint left untouched.)
  const [viewMode, setViewMode] = useState('activity'); // 'activity' | 'monthly'
  const [monthlyData, setMonthlyData] = useState({ months: [], totals: null });
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(false);

  // BILLING-UI-PREMIUM-001 — KPI sparklines from REAL monthly aggregates only
  // (GET /pricing/billing/monthly-summary). Unbilled/Ready have no history → no trend.
  // BILLING-PERF-003 Phase 2: fetched once per client AFTER the main batch settles (not in
  // parallel with Apply Filters), so the Apply critical path stays at 3 requests.
  const [kpiTrends, setKpiTrends] = useState({ invoiced: null, collected: null });
  const kpiTrendsClientRef = useRef(undefined);
  const fetchKpiTrends = (client) => {
    if (kpiTrendsClientRef.current === client) return;
    kpiTrendsClientRef.current = client;
    const params = new URLSearchParams();
    if (client && client !== 'all') params.append('client_code_id', client);
    axios.get(`${API}/pricing/billing/monthly-summary?${params.toString()}`)
      .then((res) => {
        const months = [...(res.data?.months || [])]
          .sort((a, b) => String(a.billing_month).localeCompare(String(b.billing_month)))
          .slice(-6);
        const series = (key) => {
          const pts = months.map((m) => Number(m[key] || 0));
          return pts.filter((v) => v > 0).length >= 3 ? pts : null;
        };
        setKpiTrends({ invoiced: series('invoiced_amount'), collected: series('paid_amount') });
      })
      .catch(() => { kpiTrendsClientRef.current = undefined; });
  };
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Bulk pay state (invoice-level)
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [showBulkPayConfirm, setShowBulkPayConfirm] = useState(false);
  const [bulkPaymentRef, setBulkPaymentRef] = useState('');
  const [bulkPaymentDate, setBulkPaymentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [bulkPaymentNotes, setBulkPaymentNotes] = useState('');
  const [isBulkPaying, setIsBulkPaying] = useState(false);
  
  // Audit review panel
  const [auditPanelOrder, setAuditPanelOrder] = useState(null);
  
  // Partial payment modal state (Apr 26, 2026)
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalInvoice, setPaymentModalInvoice] = useState(null);
  // Phase C.2 (Feb 2026) — Record Payment by dollar amount
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  // BULK-PAY-MODAL-001 (Sep 2026): Bulk Payment Upload opens as a modal over the
  // dashboard; /admin/billing?bulk_payments=1 (legacy page URL redirect) opens it too.
  const [showBulkPaymentUpload, setShowBulkPaymentUpload] = useState(
    () => new URLSearchParams(window.location.search).get('bulk_payments') === '1'
  );
  const [recordPaymentInvoice, setRecordPaymentInvoice] = useState(null);

  // Row selection state (for invoice generation)
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  
  // Invoice preview modal state
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  // Bill To completeness warnings — Feb 25, 2026.
  // Populated when the preview opens; one entry per client_code that has
  // critical Bill To gaps.
  const [billToWarnings, setBillToWarnings] = useState([]);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  // ─────────────────────────────────────────────────────────────────────
  // BILLING-BULK-001 (Feb 2026) — "Select all matching orders" workflow.
  // Lets accounting invoice every unbilled+priced order for the active
  // client+period filter without paginating. Backed by
  // POST /api/pricing/billing/invoices/generate-by-filter (dry-run preview
  // → confirm with preview_hash echo). One client per run; "All clients"
  // is intentionally not supported (cross-client batching is opt-in
  // per invoice in the existing selection flow).
  // ─────────────────────────────────────────────────────────────────────
  const [bulkAllMatching, setBulkAllMatching] = useState(false);
  const [showBulkInvoicePreview, setShowBulkInvoicePreview] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState(null);
  const [isLoadingBulkPreview, setIsLoadingBulkPreview] = useState(false);
  const [isGeneratingBulkInvoices, setIsGeneratingBulkInvoices] = useState(false);

  // Table sorting hooks for orders and invoices
  const {
    sortConfig: ordersSortConfig,
    handleSort: handleOrdersSort,
    getSortDirection: getOrdersSortDirection
  } = useTableSort({
    defaultSortKey: 'completed_at',
    defaultSortOrder: 'desc'
  });

  const {
    sortConfig: invoicesSortConfig,
    handleSort: handleInvoicesSort,
    getSortDirection: getInvoicesSortDirection
  } = useTableSort({
    defaultSortKey: 'created_at',
    defaultSortOrder: 'desc'
  });

  // Sort orders and invoices based on current sort configuration
  // Use sortConfig.key and sortConfig.direction (not sortKey/sortOrder)
  // BILLING-STALE-ROWS-001 — the displayed dataset is bound to the filter signature it was
  // fetched for. While the current signature differs from the loaded one (a filter/status/
  // page/search change is pending) both tables show skeleton rows — never the previous
  // filter's records. Same-signature refreshes (Refresh, Mark Paid, post-generate) keep rows.
  const filterSignature = (v) => [v.selectedClient || 'all', v.dateFrom || '', v.dateTo || '', v.billingStatusFilter || 'all', v.currentPage, v.pageSize, (v.debouncedSearch || '').trim(), v.unbilledScope || 'all'].join('|');
  const currentFilterKey = filterSignature({ selectedClient, dateFrom, dateTo, billingStatusFilter, currentPage, pageSize, debouncedSearch, unbilledScope });
  const [loadedFilterKey, setLoadedFilterKey] = useState(null);
  const staleData = loadedFilterKey !== currentFilterKey;
  // BILLING-UI-PREMIUM-004 — while the next tab/page loads, keep the previous rows on screen
  // (dimmed, non-interactive) instead of collapsing 100 rows → 4 skeleton rows → 100 rows.
  const lastOrdersRowsRef = useRef(EMPTY_ROWS);
  const lastInvoiceRowsRef = useRef(EMPTY_ROWS);

  const sortedBillableOrders = useMemo(() => {
    if (staleData) return EMPTY_ROWS;
    if (!billableOrders.length) return billableOrders;
    return sortData(billableOrders, ordersSortConfig.key, ordersSortConfig.direction);
  }, [billableOrders, ordersSortConfig, staleData]);

  // Finance-grade enhancements (Apr 24, 2026):
  // • `pageTotalAmount` — total $ value of the rows currently visible.
  //   Shown top-right of the Orders Ready for Billing header so admins can
  //   gauge the full pipeline against what's selected for invoicing.
  // • `highAmountThreshold` — 90th-percentile of the visible page amounts.
  //   Rows at or above this get a subtle indigo left accent
  //   (`border-l-2 border-indigo-400`) — pure visual priority, no logic gate.
  const pageTotalAmount = useMemo(() => {
    return sortedBillableOrders.reduce((sum, o) => {
      const price = o.total_billable_amount ?? o.billing_override_price ?? o.billing_unit_price_snapshot ?? 0;
      return sum + (Number(price) || 0);
    }, 0);
  }, [sortedBillableOrders]);

  const highAmountThreshold = useMemo(() => {
    if (sortedBillableOrders.length < 5) return Infinity; // too small to be meaningful
    const amounts = sortedBillableOrders
      .map((o) => Number(o.total_billable_amount ?? o.billing_override_price ?? o.billing_unit_price_snapshot ?? 0))
      .filter((v) => v > 0)
      .sort((a, b) => a - b);
    if (amounts.length === 0) return Infinity;
    const idx = Math.floor(amounts.length * 0.9);
    return amounts[Math.min(idx, amounts.length - 1)];
  }, [sortedBillableOrders]);

  const sortedInvoices = useMemo(() => {
    if (staleData) return EMPTY_ROWS;
    if (!invoices.length) return invoices;
    return sortData(invoices, invoicesSortConfig.key, invoicesSortConfig.direction);
  }, [invoices, invoicesSortConfig, staleData]);
  if (!staleData) { lastOrdersRowsRef.current = sortedBillableOrders; lastInvoiceRowsRef.current = sortedInvoices; }
  // Rows painted while loading: previous rows (dimmed) when we have them, else skeleton.
  const ordersDisplayRows = staleData ? lastOrdersRowsRef.current : sortedBillableOrders;
  const invoicesDisplayRows = staleData ? lastInvoiceRowsRef.current : sortedInvoices;
  const ordersHoldPrev = staleData && ordersDisplayRows.length > 0;
  const invoicesHoldPrev = staleData && invoicesDisplayRows.length > 0;
  
  useEffect(() => {
    if (hasAccess) {
      fetchClientCodes();
    }
  }, [hasAccess]);
  
  // Debounce search input (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  useEffect(() => {
    if (hasAccess) {
      setSelectedOrderIds(new Set());
      fetchBillingData();
    }
  }, [hasAccess, selectedClient, dateFrom, dateTo, billingStatusFilter, currentPage, pageSize, debouncedSearch, unbilledScope]);
  
  // (Aging Buckets fetch removed with the strip — Jun 2026.)
  
  // Phase B UI — fetch Monthly summary when entering Monthly view or filters change
  useEffect(() => {
    if (!hasAccess || viewMode !== 'monthly') return;
    setIsMonthlyLoading(true);
    const params = new URLSearchParams();
    if (selectedClient && selectedClient !== 'all') {
      params.append('client_code_id', selectedClient);
    }
    if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
    if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
    axios.get(`${API}/pricing/billing/monthly-summary?${params.toString()}`)
      .then((res) => setMonthlyData(res.data || { months: [], totals: null }))
      .catch((err) => console.error('Error fetching monthly summary:', err))
      .finally(() => setIsMonthlyLoading(false));
  }, [hasAccess, viewMode, selectedClient, dateFrom, dateTo]);

  // BILLING-PERF-003 Phase 2 — ONE consolidated, visibility-aware exception-count poller.
  // Sequential (one Mongo op at a time), aborts stale runs, pauses while the tab is hidden
  // or an Apply is in flight, backs off to 5 min after 2 consecutive all-failed ticks.
  // A failed/degraded count keeps its last value and flags `error` (card shows Retry).
  const countsAbortRef = useRef(null);
  const countsTimerRef = useRef(null);
  const countsFailStreakRef = useRef(0);
  const countsLastRunRef = useRef(0);
  const fetchExceptionCounts = useCallback(async (only = null) => {
    if (!countsReady) return;
    countsAbortRef.current?.abort();
    const ctrl = new AbortController();
    countsAbortRef.current = ctrl;
    countsLastRunRef.current = Date.now();
    // Phase 4: ONE /billing/summary request refreshes every exception count (and KPIs).
    if (kpiParamsRef.current !== null) {
      setExceptionCounts((p) => ({ ...p, missing: { ...p.missing, refreshing: true }, qc: { ...p.qc, refreshing: true }, delivery: { ...p.delivery, refreshing: true } }));
      try {
        const res = await axios.get(`${API}/pricing/billing/summary?${kpiParamsRef.current}`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        applySummary(res.data || null);
        countsFailStreakRef.current = 0;
      } catch (err) {
        if (ctrl.signal.aborted || axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        const msg = err?.response?.status ? `HTTP ${err.response.status}` : 'network';
        setExceptionCounts((p) => ({ ...p, missing: { ...p.missing, refreshing: false, error: msg }, qc: { ...p.qc, refreshing: false, error: msg }, delivery: { ...p.delivery, refreshing: false, error: msg } }));
        countsFailStreakRef.current += 1;
      }
      return;
    }
    const specs = [
      ['missing', 'missing-pricing/count', (d) => (d?.count ?? null)],
      ['qc', 'qc-gated/count', (d) => (d?.total == null ? null : { total: d.total || 0, held: d.held || 0, dollars: d.dollars || 0 })],
      ['delivery', 'delivery-exceptions/count', (d) => (d?.count ?? null)],
    ].filter(([k]) => !only || k === only);
    let failures = 0;
    for (const [key, path, pick] of specs) {
      setExceptionCounts((p) => ({ ...p, [key]: { ...p[key], refreshing: true } }));
      try {
        const res = await axios.get(`${API}/pricing/billing/${path}`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        const value = res.data?.degraded ? null : pick(res.data);
        if (value == null) failures++;
        setExceptionCounts((p) => ({
          ...p,
          [key]: value == null
            ? { ...p[key], refreshing: false, error: res.data?.stage || 'unavailable' }
            : { value, refreshing: false, error: null },
        }));
      } catch (err) {
        if (ctrl.signal.aborted || axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        failures++;
        setExceptionCounts((p) => ({ ...p, [key]: { ...p[key], refreshing: false, error: err?.response?.status ? `HTTP ${err.response.status}` : 'network' } }));
      }
    }
    if (!only) countsFailStreakRef.current = failures === specs.length ? countsFailStreakRef.current + 1 : 0;
  }, [countsReady]);
  const fetchQcGatedCount = useCallback(() => fetchExceptionCounts('qc'), [fetchExceptionCounts]);

  useEffect(() => {
    if (!countsReady) return undefined;
    let cancelled = false;
    const schedule = (ms) => { clearTimeout(countsTimerRef.current); countsTimerRef.current = setTimeout(tick, ms); };
    async function tick() {
      if (cancelled || document.hidden) return; // resumed by visibilitychange
      if (isLoadingRef.current) { schedule(5_000); return; } // never overlap Apply Filters
      await fetchExceptionCounts();
      if (!cancelled) schedule(countsFailStreakRef.current >= 2 ? 300_000 : 60_000);
    }
    const onVisibility = () => {
      if (document.hidden) { clearTimeout(countsTimerRef.current); return; }
      const elapsed = Date.now() - countsLastRunRef.current;
      if (elapsed >= 60_000) tick(); else schedule(60_000 - elapsed);
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Phase 4: the initial batch already loaded /billing/summary — first poll tick waits a full interval.
    if (kpiParamsRef.current !== null) { countsLastRunRef.current = Date.now(); schedule(60_000); } else tick();
    return () => {
      cancelled = true;
      clearTimeout(countsTimerRef.current);
      countsAbortRef.current?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [countsReady, fetchExceptionCounts]);

  // When user switches to the delivery-exceptions view, fetch the list.
  useEffect(() => {
    if (!hasAccess || activeKpi !== 'delivery-exceptions') return;
    setDeliveryExceptionsLoading(true);
    axios.get(`${API}/pricing/billing/delivery-exceptions?limit=200`)
      .then((res) => setDeliveryExceptions(res.data?.orders || []))
      .catch((err) => console.error('Error fetching delivery exceptions:', err))
      .finally(() => setDeliveryExceptionsLoading(false));
  }, [hasAccess, activeKpi]);

  // When user switches to the missing-pricing view, fetch the focused list.
  useEffect(() => {
    if (!hasAccess || activeKpi !== 'missing-pricing') return;
    setMissingPricingLoading(true);
    axios.get(`${API}/pricing/billing/orders?pricing_state=missing&limit=200`)
      .then((res) => setMissingPricingOrders(res.data?.orders || []))
      .catch((err) => console.error('Error fetching missing pricing orders:', err))
      .finally(() => setMissingPricingLoading(false));
  }, [hasAccess, activeKpi]);

  // Manual "Recalculate All Missing Pricing" safety valve — Feb 2026.
  // Sweeps every missing-pricing order through the pricing engine in a
  // single click. Invoice safety is preserved by the backend
  // (apply_pricing_to_order skips invoiced/locked/overridden rows).
  const handleRecalculateAllMissingPricing = async () => {
    setIsRecalculatingAll(true);
    try {
      // PERF-ASYNC-REPAIRS-001: endpoint now returns a run_id immediately;
      // poll the run until it completes (keeps the gateway from timing out).
      const res = await axios.post(`${API}/pricing/billing/missing-pricing/recalculate`);
      const runId = res.data?.run_id;
      let s = res.data?.summary || {};
      if (runId) {
        const deadline = Date.now() + 10 * 60 * 1000;
        let run = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          const poll = await axios.get(`${API}/pricing/billing/repair/runs/${runId}`);
          run = poll.data;
          if (run?.status !== 'running') break;
        }
        if (!run || run.status === 'running') {
          toast.info('Recalculation is still running in the background — refresh the page in a minute to see results.');
          return;
        }
        if (run.status === 'failed') {
          toast.error(`Recalculation failed: ${run.error || 'unknown error'}`);
          return;
        }
        s = run.report?.summary || {};
      }
      const recalculated = s.recalculated || 0;
      const stillMissing = s.still_missing || 0;
      if (recalculated > 0) {
        toast.success(
          `Recalculated ${recalculated} ${recalculated === 1 ? 'order' : 'orders'}` +
          (stillMissing > 0 ? ` — ${stillMissing} still missing pricing (no rule configured).` : '.')
        );
      } else if (stillMissing > 0) {
        toast.info(
          `No orders could be auto-priced — ${stillMissing} still need a pricing rule or override.`
        );
      } else {
        toast.info('No missing-pricing orders to recalculate.');
      }
      // Refetch queue + count
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/pricing/billing/orders?pricing_state=missing&limit=200`),
        axios.get(`${API}/pricing/billing/missing-pricing/count`),
      ]);
      setMissingPricingOrders(listRes.data?.orders || []);
      setMissingPricingCount(countRes.data?.count || 0);
      fetchBillingData();
    } catch (error) {
      console.error('Recalculate-all failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to recalculate missing pricing');
    } finally {
      setIsRecalculatingAll(false);
    }
  };

  /* Jul 2026 — BILLING-EXCLUDE-001: admin action to exclude test orders
     from the billing workflow (reversible, reason required, audited). */
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [isExcluding, setIsExcluding] = useState(false);
  const handleConfirmExclude = async () => {
    if (!excludeTarget) return;
    const reason = excludeReason.trim();
    if (reason.length < 5) {
      toast.error('Please provide a reason (at least 5 characters).');
      return;
    }
    setIsExcluding(true);
    try {
      const res = await axios.post(
        `${API}/pricing/billing/orders/${excludeTarget.id}/exclude`,
        { reason },
      );
      toast.success(res?.data?.message || 'Order excluded from billing.');
      setExcludeTarget(null);
      setExcludeReason('');
      fetchBillingData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to exclude order from billing.');
    } finally {
      setIsExcluding(false);
    }
  };

  const fetchClientCodes = async () => {
    try {
      const response = await axios.get(`${API}/client-codes`);
      setClientCodes(response.data || []);
    } catch (error) {
      console.error('Error fetching client codes:', error);
    }
  };
  
  const fetchBillingData = async (force = false) => {
    const seq = ++fetchSeqRef.current;
    fetchAbortRef.current?.abort();
    const ctrl = new AbortController();
    fetchAbortRef.current = ctrl;
    const requestFilterKey = filterSignature({ selectedClient, dateFrom, dateTo, billingStatusFilter, currentPage, pageSize, debouncedSearch, unbilledScope });
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') {
        params.append('client_code_id', selectedClient);
      }
      if (dateFrom) {
        params.append('date_from', new Date(dateFrom).toISOString());
      }
      if (dateTo) {
        params.append('date_to', new Date(dateTo).toISOString());
      }
      if (billingStatusFilter && billingStatusFilter !== 'all') {
        params.append('billing_status', billingStatusFilter);
      }
      // Feb 2026 — surface $0.00 / Missing Pricing orders in the
      // Unbilled list with a red badge. Invoice generation still
      // strictly rejects them server-side.
      params.append('pricing_state', 'all');
      if (debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      if (unbilledPeriodActive) params.append('period_scope', 'period');  // BILLING-UNBILLED-FILTER-002
      
      // Feb 2026 — BILLING-RECON-001 (Fix C) — KPI now comes from a
      // dedicated aggregation endpoint that scopes to the FULL filtered
      // dataset (not just the current page). Mirrors the same client /
      // date / search / pricing_state filters as the orders table so
      // KPI ↔ table reconciliation is guaranteed.
      const kpiParams = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') {
        kpiParams.append('client_code_id', selectedClient);
      }
      if (dateFrom) kpiParams.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) kpiParams.append('date_to', new Date(dateTo).toISOString());
      if (debouncedSearch.trim()) kpiParams.append('search', debouncedSearch.trim());
      kpiParams.append('pricing_state', 'all');
      if (unbilledPeriodActive) kpiParams.append('period_scope', 'period');

      
      const invoiceParams = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') {
        invoiceParams.append('client_code_id', selectedClient);
      }
      // Phase A (Feb 2026) — date filter now applies to invoices too
      // (filters on invoice_date = generation timestamp).
      if (dateFrom) {
        invoiceParams.append('date_from', new Date(dateFrom).toISOString());
      }
      if (dateTo) {
        invoiceParams.append('date_to', new Date(dateTo).toISOString());
      }
      // BILLING-STATUS-TABS-001 — Paid must never show unpaid invoices. Server filter
      // (`invoice_status`, single value) for Paid; Invoiced = all NOT-paid invoices
      // (invoiced / partially_paid / sent / overdue) via a client-side guard because the
      // endpoint accepts one status value; All = no restriction.
      if (billingStatusFilter === 'paid') invoiceParams.append('invoice_status', 'paid');
      invoiceParams.append('page', '1');
      invoiceParams.append('limit', '50');
      
      // BILLING-PERF-003 Phase 2: the three requests are independent AND isolated —
      // allSettled so one failure never blanks the others; every call carries the abort
      // signal so a newer Apply cancels these on the wire.
      const opts = { signal: ctrl.signal };
      // BILLING-UI-PREMIUM-004 — the summary (KPIs) does not depend on the status tab or page:
      // only show the KPI "refreshing" state when its inputs actually changed (or on explicit Refresh).
      const kpiKey = kpiParams.toString();
      const kpiInputsChanged = force === true || kpiKey !== kpiParamsRef.current;
      setKpiRefreshing(kpiInputsChanged);
      kpiParamsRef.current = kpiKey;
      const [ordersRes, kpiRes, invoicesRes] = await Promise.allSettled([
        axios.get(`${API}/pricing/billing/orders?${params.toString()}`, opts),
        axios.get(`${API}/pricing/billing/summary?${kpiParams.toString()}`, opts), // Phase 4: KPIs + exception counts + totals in ONE request
        axios.get(`${API}/pricing/billing/invoices?${invoiceParams.toString()}`, opts),
      ]);
      if (seq !== fetchSeqRef.current || ctrl.signal.aborted) return; // stale — a newer request superseded this one
      const errText = (reason) => reason?.response?.data?.detail || (reason?.response?.status ? `HTTP ${reason.response.status}` : 'Network error');

      if (ordersRes.status === 'fulfilled') {
        setBillableOrders(ordersRes.value.data?.orders || []);
        setTotalPages(ordersRes.value.data?.pages || 1);
        setTotalCount(ordersRes.value.data?.total || 0);
        setOrdersError(null);
      } else {
        // BILLING-STALE-COUNT-FIX (Aug 2026): never leave the previous (unfiltered)
        // rows/total on screen when a filtered request fails.
        console.error('Error fetching billing orders:', ordersRes.reason);
        setBillableOrders([]);
        setTotalCount(0);
        setTotalPages(1);
        setOrdersError(errText(ordersRes.reason));
      }

      let rawInvoices = null;
      if (invoicesRes.status === 'fulfilled') {
        rawInvoices = invoicesRes.value.data?.invoices || [];
        setInvoices(billingStatusFilter === 'invoiced' ? rawInvoices.filter((inv) => inv.status !== 'paid') : rawInvoices);
        setInvoicesError(null);
      } else {
        console.error('Error fetching invoices:', invoicesRes.reason);
        setInvoices([]);
        setInvoicesError(errText(invoicesRes.reason));
      }
      setLoadedFilterKey(requestFilterKey); // same batch as the rows → atomic swap

      if (kpiRes.status === 'fulfilled') {
        applySummary(kpiRes.value.data || null);
      } else {
        console.warn('[Billing KPI] aggregator failed — keeping last known values', kpiRes.reason);
        kpiParamsRef.current = '';  // next fetch (Retry) shows the refreshing state again
        setKpiError(errText(kpiRes.reason));
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current || ctrl.signal.aborted) return;
      console.error('Error preparing billing request:', error);
      setOrdersError('Failed to load billing data');
      setLoadedFilterKey(requestFilterKey);
    } finally {
      if (seq === fetchSeqRef.current && !ctrl.signal.aborted) {
        setIsLoading(false);
        setKpiRefreshing(false);
        setTableLoadedOnce(true);
        fetchKpiTrends(selectedClient); // sparklines: only when the client changed, after the batch
      }
    }
  };

  // BILLING-PERF-003 Phase 2 — merge KPI blocks into `stats`. A block the backend marked
  // degraded (null) keeps its previous value; the card shows Retry. There is NO page-bound
  // fallback anymore: an unknown value is rendered as "—", never as $0.00 / 0.
  const applyKpiData = (k) => {
    if (!k || (!k.unbilled && !k.invoiced && !k.paid)) { setKpiError('unavailable'); return; }
    setStats((prev) => ({
      readyCount: k.unbilled ? (k.unbilled.count || 0) : prev.readyCount,
      readyAmount: k.unbilled ? (k.unbilled.amount || 0) : prev.readyAmount,
      backlogCount: k.unbilled_backlog ? (k.unbilled_backlog.count || 0) : null,
      backlogAmount: k.unbilled_backlog ? (k.unbilled_backlog.amount || 0) : null,
      invoicedCount: k.invoiced ? (k.invoiced.count || 0) : prev.invoicedCount,
      invoicedAmount: k.invoiced ? (k.invoiced.amount || 0) : prev.invoicedAmount,
      paidCount: k.paid ? (k.paid.count || 0) : prev.paidCount,
      paidAmount: k.paid ? (k.paid.amount || 0) : prev.paidAmount,
    }));
    const degraded = Array.isArray(k.degraded) ? k.degraded : [];
    setKpiError(degraded.length ? `partial: ${degraded.join(', ')}` : null);
  };
  const [kpiRefreshing, setKpiRefreshing] = useState(false);
  const kpiStatus = (isLoading && kpiRefreshing) ? 'refreshing' : (kpiError ? 'error' : null);
  // Phase 4 — GET /billing/summary carries kpis + exceptions (+ table totals). Blocks that are
  // null/degraded keep their last known value and surface Retry, exactly like before.
  const kpiParamsRef = useRef('');
  const applySummary = (sm) => {
    if (!sm) { setKpiError('unavailable'); return; }
    const degraded = Array.isArray(sm.degraded) ? sm.degraded : [];
    applyKpiData({ ...(sm.kpis || {}), degraded: degraded.filter((d) => d.startsWith('kpi')) });
    const ex = sm.exceptions || {};
    const has = (v) => v !== null && v !== undefined;
    setExceptionCounts((p) => ({
      missing: has(ex.missing_pricing) ? { value: ex.missing_pricing, error: null, refreshing: false } : { ...p.missing, refreshing: false, error: 'unavailable' },
      qc: has(ex.qc_gated) ? { value: { total: ex.qc_gated.total || 0, held: ex.qc_gated.held || 0, dollars: ex.qc_gated.dollars || 0 }, error: null, refreshing: false } : { ...p.qc, refreshing: false, error: 'unavailable' },
      delivery: has(ex.not_delivered) ? { value: ex.not_delivered, error: null, refreshing: false } : { ...p.delivery, refreshing: false, error: 'unavailable' },
      unclassified: { value: has(ex.unclassified) ? ex.unclassified : null, error: null, refreshing: false },
    }));
    setSummaryMeta(sm.meta || null);
  };
  const [summaryMeta, setSummaryMeta] = useState(null);
  
  
  const handleMarkPaid = (invoice) => {
    // Apr 26, 2026 — opens partial-payment modal instead of confirm dialog.
    // Accepts the full invoice doc (was: just invoiceId) so the modal has
    // access to line_items + paid flags without an extra fetch.
    setPaymentModalInvoice(invoice);
    setShowPaymentModal(true);
  };
  
  const handlePaymentApplied = () => {
    fetchBillingData();
  };
  
  // Phase C.2 — open Record Payment modal for dollar-amount entry
  const handleRecordPayment = (invoice) => {
    setRecordPaymentInvoice(invoice);
    setShowRecordPaymentModal(true);
  };
  
  // After a payment is recorded, refresh: invoice rows + monthly.
  const handleRecordPaymentApplied = useCallback(() => {
    fetchBillingData();
    // Refresh monthly summary if the user is currently looking at it
    if (viewMode === 'monthly') {
      const mp = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') mp.append('client_code_id', selectedClient);
      if (dateFrom) mp.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) mp.append('date_to', new Date(dateTo).toISOString());
      axios.get(`${API}/pricing/billing/monthly-summary?${mp.toString()}`)
        .then((res) => setMonthlyData(res.data || { months: [], totals: null }))
        .catch(() => {});
    }
  }, [selectedClient, viewMode, dateFrom, dateTo]);
  
  // Invoice selection helpers (for bulk mark paid)
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };
  
  const toggleSelectAllInvoices = () => {
    // Apr 26, 2026 — partially_paid invoices are also eligible for
    // bulk mark-paid (the backend now skips already-paid orders).
    const eligibleInvoices = sortedInvoices.filter(inv => inv.status === 'invoiced' || inv.status === 'partially_paid');
    if (selectedInvoiceIds.size === eligibleInvoices.length && eligibleInvoices.length > 0) {
      setSelectedInvoiceIds(new Set());
    } else {
      setSelectedInvoiceIds(new Set(eligibleInvoices.map(inv => inv.id)));
    }
  };
  
  const selectedInvoices = useMemo(() => {
    return sortedInvoices.filter(inv => selectedInvoiceIds.has(inv.id));
  }, [sortedInvoices, selectedInvoiceIds]);
  
  const selectedInvoicesTotal = useMemo(() => {
    return selectedInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  }, [selectedInvoices]);
  
  const handleBulkMarkInvoicesPaid = async () => {
    if (selectedInvoiceIds.size === 0) return;
    
    setIsBulkPaying(true);
    try {
      const response = await axios.post(`${API}/pricing/billing/bulk-mark-paid`, {
        invoice_ids: Array.from(selectedInvoiceIds),
        payment_reference: bulkPaymentRef || null,
        payment_date: bulkPaymentDate ? new Date(bulkPaymentDate).toISOString() : null,
        notes: bulkPaymentNotes || null
      });
      
      const updated = response.data.updated_invoices?.length || 0;
      toast.success(`${updated} invoice(s) marked as paid`);
      setShowBulkPayConfirm(false);
      setSelectedInvoiceIds(new Set());
      setBulkPaymentRef('');
      setBulkPaymentNotes('');
      fetchBillingData();
    } catch (error) {
      console.error('Error bulk marking invoices paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk payment');
    } finally {
      setIsBulkPaying(false);
    }
  };
  
  // Row selection helpers
  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  
  const toggleSelectAll = () => {
    // Only select unbilled, fully-priced orders (eligible for invoicing).
    // Missing-pricing orders are visible in the queue but never selectable —
    // backend strictly refuses to invoice them.
    const eligibleOrders = sortedBillableOrders.filter(o =>
      !o.invoice_id
      && (o.billing_status === 'unbilled' || !o.billing_status)
      && !o.is_missing_pricing
    );
    if (selectedOrderIds.size === eligibleOrders.length && eligibleOrders.length > 0) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(eligibleOrders.map(o => o.id)));
    }
  };
  
  const selectedOrders = useMemo(() => {
    return sortedBillableOrders.filter(o => selectedOrderIds.has(o.id));
  }, [sortedBillableOrders, selectedOrderIds]);
  
  const selectedTotal = useMemo(() => {
    return selectedOrders.reduce((sum, o) => sum + (o.total_billable_amount || o.billing_override_price || o.billing_unit_price_snapshot || 0), 0);
  }, [selectedOrders]);
  
  // Group selected orders by client for preview
  const selectedByClient = useMemo(() => {
    const groups = {};
    selectedOrders.forEach(o => {
      const cc = o.client_code || 'Unknown';
      if (!groups[cc]) groups[cc] = { orders: [], total: 0 };
      groups[cc].orders.push(o);
      groups[cc].total += (o.total_billable_amount || o.billing_override_price || o.billing_unit_price_snapshot || 0);
    });
    return groups;
  }, [selectedOrders]);
  
  // Open invoice preview modal
  const openInvoicePreview = async () => {
    if (selectedOrderIds.size === 0) return;
    setInvoicePreviewData({
      orders: selectedOrders,
      byClient: selectedByClient,
      total: selectedTotal,
      count: selectedOrders.length
    });
    setBillToWarnings([]);
    setShowInvoicePreview(true);

    // Fetch Bill To completeness for each unique client in the selection.
    // Non-blocking: failures shouldn't prevent the user from generating.
    const uniqueClients = Object.keys(selectedByClient || {});
    try {
      const results = await Promise.all(
        uniqueClients.map(async (clientCode) => {
          try {
            const resp = await axios.get(
              `${API}/pricing/billing/client-billing-completeness`,
              { params: { client_code: clientCode } }
            );
            return resp.data;
          } catch (e) {
            return null;
          }
        })
      );
      const warnings = (results || [])
        .filter((r) => r && r.has_critical_gaps)
        .map((r) => ({
          client_code: r.client_code,
          client_code_id: r.client_code_id,
          client_name: r.client_name,
          missing_critical: r.missing_critical || [],
          missing_recommended: r.missing_recommended || [],
        }));
      setBillToWarnings(warnings);
    } catch (e) {
      // Silent — completeness check is advisory only.
    }
  };
  
  const [invoiceGenSuccess, setInvoiceGenSuccess] = useState(null);

  // Confirm and generate invoice from selected orders
  const handleConfirmGenerateInvoice = async () => {
    if (selectedOrderIds.size === 0) return;
    
    setIsGeneratingInvoice(true);
    try {
      const response = await axios.post(`${API}/pricing/billing/invoices/generate`, {
        order_ids: Array.from(selectedOrderIds),
        start_date: dateFrom,
        end_date: dateTo
      });
      
      const invoices = response.data?.invoices || [];
      if (invoices.length === 1) {
        toast.success(`Invoice ${invoices[0].invoice_number} generated — ${invoices[0].orders_count} orders, ${formatCurrency(invoices[0].total_amount)}`);
      } else {
        toast.success(`Generated ${invoices.length} invoices`);
      }

      // BILLING-UI-PREMIUM-001 — brief in-dialog success confirmation before closing
      setInvoiceGenSuccess(invoices);
      await new Promise((r) => setTimeout(r, prefersReducedMotion() ? 400 : 1100));
      setInvoiceGenSuccess(null);
      setShowInvoicePreview(false);
      setSelectedOrderIds(new Set());
      // BILLING-SYNC-001 (Feb 2026): set the status filter alongside the tab
      // so the Generated Invoices view also re-fetches and the user lands
      // on a synchronized state (was: setActiveTab only → filter pill said
      // "Unbilled" while the invoices tab was active).
      setBillingStatusFilter('invoiced');
      setActiveTab('invoices');
      setActiveKpi('invoiced');
      fetchBillingData();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // BILLING-BULK-001 — Bulk preview / generate handlers
  // ─────────────────────────────────────────────────────────────────────

  // Eligible orders visible on the current page (mirrors toggleSelectAll).
  const pageEligibleOrders = useMemo(() => sortedBillableOrders.filter(o =>
    !o.invoice_id
    && (o.billing_status === 'unbilled' || !o.billing_status)
    && !o.is_missing_pricing
  ), [sortedBillableOrders]);

  // Banner visible only when the user has selected every eligible row on
  // the current page AND there are more matching orders beyond this page.
  const showBulkAllMatchingBanner = (
    billingView === 'external'
    && billingStatusFilter === 'unbilled'
    && pageEligibleOrders.length > 0
    && selectedOrderIds.size === pageEligibleOrders.length
    && totalCount > pageEligibleOrders.length
    && selectedClient && selectedClient !== 'all'
  );

  const openBulkInvoicePreview = async () => {
    if (!selectedClient || selectedClient === 'all') {
      toast.error('Select a single client to invoice all matching orders.');
      return;
    }
    setIsLoadingBulkPreview(true);
    setBillToWarnings([]);
    try {
      // BILLING-UNBILLED-BACKLOG-001: under Unbilled the bulk action targets the client's whole
      // eligible outstanding backlog (+ current search), regardless of completion date.
      const resp = await axios.post(`${API}/pricing/billing/invoices/generate-by-filter`, {
        client_code_id: selectedClient,
        date_from: (isOutstandingQueue && !unbilledPeriodActive) ? null : dateFrom,
        date_to: (isOutstandingQueue && !unbilledPeriodActive) ? null : dateTo,
        outstanding: isOutstandingQueue && !unbilledPeriodActive,  // BILLING-UNBILLED-FILTER-002
        search: debouncedSearch?.trim() || null,
        dry_run: true,
      });
      const preview = resp.data?.preview;
      if (!preview) {
        toast.error('Unable to load preview.');
        return;
      }
      setBulkPreviewData(preview);
      if (preview.bill_to_completeness?.has_critical_gaps) {
        setBillToWarnings([{
          client_code: preview.client_code,
          client_code_id: preview.client_code_id,
          client_name: preview.client_name,
          missing_critical: preview.bill_to_completeness.missing_critical || [],
          missing_recommended: preview.bill_to_completeness.missing_recommended || [],
        }]);
      }
      setShowBulkInvoicePreview(true);
    } catch (error) {
      console.error('Bulk preview failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to load bulk preview');
    } finally {
      setIsLoadingBulkPreview(false);
    }
  };

  const handleConfirmBulkGenerate = async () => {
    if (!bulkPreviewData?.preview_hash) {
      toast.error('Preview expired — please re-open the preview.');
      return;
    }
    setIsGeneratingBulkInvoices(true);
    try {
      const resp = await axios.post(`${API}/pricing/billing/invoices/generate-by-filter`, {
        client_code_id: bulkPreviewData.client_code_id,
        date_from: bulkPreviewData.outstanding ? null : bulkPreviewData.date_from,
        date_to: bulkPreviewData.outstanding ? null : bulkPreviewData.date_to,
        outstanding: Boolean(bulkPreviewData.outstanding),
        search: bulkPreviewData.search || null,
        dry_run: false,
        preview_hash: bulkPreviewData.preview_hash,
      });
      const created = resp.data?.invoices || [];
      toast.success(
        `Generated ${created.length} invoice(s) covering ${resp.data?.orders_count || 0} orders`
      );

      // CSV export of the generated invoices (BILLING-BULK-001 deliverable).
      try {
        const header = ['invoice_number', 'client_code', 'client_name', 'orders_count', 'total_amount', 'chunk_index'];
        const lines = [header.join(',')];
        created.forEach((inv) => {
          lines.push([
            inv.invoice_number,
            inv.client_code,
            JSON.stringify(inv.client_name || ''),
            inv.orders_count,
            inv.total_amount,
            inv.chunk_index,
          ].join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulk_invoices_${bulkPreviewData.client_code}_${bulkPreviewData.date_from}_${bulkPreviewData.date_to}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (csvErr) {
        console.warn('CSV export failed:', csvErr);
      }

      setShowBulkInvoicePreview(false);
      setBulkPreviewData(null);
      setSelectedOrderIds(new Set());
      setBulkAllMatching(false);
      setBillingStatusFilter('invoiced');
      setActiveTab('invoices');
      setActiveKpi('invoiced');
      fetchBillingData();
    } catch (error) {
      console.error('Bulk generate failed:', error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'preview_hash_mismatch') {
        toast.error('The eligible order set has changed. Please re-open the preview.');
        setShowBulkInvoicePreview(false);
        setBulkPreviewData(null);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to generate invoices'));
      }
    } finally {
      setIsGeneratingBulkInvoices(false);
    }
  };

  // Jul 2026 — BILLING-STATEMENT-XLSX-001: formatted client-facing Excel
  // statement (Bill To auto-populated from the client code's Customer
  // Billing fields, vendor from Billing Settings, split address columns,
  // Grand Total Outstanding). Respects the same filters as the CSV export.
  const handleExportXLSX = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') params.append('client_code_id', selectedClient);
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
      if (billingStatusFilter && billingStatusFilter !== 'all') params.append('billing_status', billingStatusFilter);
      if (debouncedSearch && debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
      if (unbilledPeriodActive) params.append('period_scope', 'period');

      const response = await axios.get(`${API}/pricing/billing/export-xlsx?${params.toString()}`, {
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `Billing_Statement_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      const total = response.headers['x-total-records'];
      toast.success(`Billing statement exported${total ? ` (${total} orders)` : ''}`);
    } catch (error) {
      console.error('Error exporting statement:', error);
      toast.error('Failed to export billing statement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setIsLoading(true);
    try {
      // Apr 26, 2026 — CSV export now respects every dashboard filter
      // (client, date range, status, search, view) so the exported
      // row-set matches exactly what the user sees on screen.
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') params.append('client_code_id', selectedClient);
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
      if (billingStatusFilter && billingStatusFilter !== 'all') params.append('billing_status', billingStatusFilter);
      if (debouncedSearch && debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
      if (billingView) params.append('view', billingView);
      if (unbilledPeriodActive) params.append('period_scope', 'period');
      
      const response = await axios.get(`${API}/pricing/billing/export?${params.toString()}`);
      
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.total_records} records`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export billing data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Phase B UI follow-up (Feb 2026) — Export the Monthly summary as
  // CSV (client-side; the data is already in `monthlyData`). Respects
  // the current client + date filters since `monthlyData` is fetched
  // with them applied.
  const handleExportMonthlyCSV = () => {
    const months = monthlyData?.months || [];
    if (!months.length) {
      toast.error('No monthly data to export');
      return;
    }
    const headers = ['Month', 'Invoiced', 'Paid', 'Outstanding', 'Invoices', 'Payments', 'Collection Rate %'];
    const rows = months
      .slice()
      .sort((a, b) => b.billing_month.localeCompare(a.billing_month))
      .map((m) => {
        const rate = m.invoiced_amount > 0
          ? ((m.paid_amount / m.invoiced_amount) * 100).toFixed(1)
          : '0.0';
        return [
          formatMonthLabel(m.billing_month),
          m.invoiced_amount.toFixed(2),
          m.paid_amount.toFixed(2),
          m.balance_amount.toFixed(2),
          m.invoice_count,
          m.payment_count,
          rate,
        ];
      });
    const totals = monthlyData?.totals;
    if (totals) {
      const rate = totals.invoiced_amount > 0
        ? ((totals.paid_amount / totals.invoiced_amount) * 100).toFixed(1)
        : '0.0';
      rows.push([
        'Total',
        totals.invoiced_amount.toFixed(2),
        totals.paid_amount.toFixed(2),
        totals.balance_amount.toFixed(2),
        totals.invoice_count,
        totals.payment_count,
        rate,
      ]);
    }
    const escape = (v) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().split('T')[0];
    a.download = `billing-monthly-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${months.length} ${months.length === 1 ? 'month' : 'months'}`);
  };
  
  // INV-PDF-REMOVE-001: handleDownloadInvoicePDF removed (PDF download retired)
  
  // BILLING-INVOICE-XLSX-001 (Sep 2026) — original Excel statement for ONE invoice,
  // rendered from the invoice snapshot (never live pricing). Read-only GET.
  const handleDownloadInvoiceXLSX = async (invoiceId, invoiceNumber) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/pricing/billing/invoice/${invoiceId}/xlsx`, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `${invoiceNumber || 'invoice'}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice Excel:', error);
      toast.error('Failed to download invoice Excel');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadSummaryPDF = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient && selectedClient !== 'all') params.append('client_code_id', selectedClient);
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
      
      const response = await axios.get(`${API}/pricing/billing/summary/pdf?${params.toString()}`, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_summary_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // Jun 14, 2026 — FILES-TOAST-001: removed "Summary PDF downloaded" toast.
    } catch (error) {
      console.error('Error downloading summary PDF:', error);
      toast.error('Failed to download summary PDF');
    } finally {
      setIsLoading(false);
    }
  };
  
  // BILLING-INVOICE-EMAIL-001: proper Send Invoice modal (recipients resolved server-side).
  const [emailInvoiceTarget, setEmailInvoiceTarget] = useState(null);
  const handleEmailInvoice = (invoiceId, invoiceNumber) => {
    const inv = invoices.find((i) => i.id === invoiceId) || { id: invoiceId, invoice_number: invoiceNumber };
    setEmailInvoiceTarget(inv);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return formatDateOnly(dateStr);
  };
  
  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetailsDialog(true);
  };
  
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">Access Denied</h2>
          <p className="text-slate-500 mt-2">Accounting or Admin role required.</p>
        </div>
      </div>
    );
  }
  
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50" data-testid="billing-dashboard">
        {/* Feb 2026 — Removed the standalone Billing Dashboard sub-
            header (oversized H1 + subtitle + detached export buttons).
            The page now opens directly into the data: a compact
            "Billing" label and export actions are inlined into the
            filter strip below so they read as scoped to the active
            client/date filters. The global app header already
            contextually identifies the page. */}
        
        <div className="max-w-[1440px] mx-auto px-6 pt-4 pb-8 space-y-3.5">
          {/* BILLING-UI-PREMIUM-003 (Nov 2026) — navy hero with animated waves + floating invoice illustration */}
          <div className="bd-enter bd-hero relative overflow-hidden rounded-2xl px-5 py-3.5 flex items-center gap-4 text-white min-h-[96px]" data-testid="billing-page-header">
            <svg className="bd-hero-waves" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true" focusable="false">
              <g className="bd-wave bd-wave-a">
                <path d="M0 78 C 220 20, 420 130, 640 70 S 1060 10, 1440 78 L1440 120 L0 120 Z" fill="#7C3AED" opacity="0.22" />
                <path d="M0 78 C 220 20, 420 130, 640 70 S 1060 10, 1440 78" fill="none" stroke="#A78BFA" strokeOpacity="0.85" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M1440 78 C 1660 20, 1860 130, 2080 70 S 2500 10, 2880 78 L2880 120 L1440 120 Z" fill="#7C3AED" opacity="0.22" />
                <path d="M1440 78 C 1660 20, 1860 130, 2080 70 S 2500 10, 2880 78" fill="none" stroke="#A78BFA" strokeOpacity="0.85" strokeWidth="2.5" strokeLinecap="round" />
              </g>
              <g className="bd-wave bd-wave-b">
                <path d="M0 92 C 260 40, 520 120, 780 80 S 1220 30, 1440 92" fill="none" stroke="#38BDF8" strokeOpacity="0.55" strokeWidth="1.5" />
                <path d="M1440 92 C 1700 40, 1960 120, 2220 80 S 2660 30, 2880 92" fill="none" stroke="#38BDF8" strokeOpacity="0.55" strokeWidth="1.5" />
              </g>
            </svg>
            <span className="bd-hero-icon relative z-10 w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
              <Receipt className="w-6 h-6 text-white" />
            </span>
            <div className="relative z-10 min-w-0 pr-0 lg:pr-[240px]">
              <h1 className="text-2xl sm:text-[28px] font-bold tracking-tight leading-8">Billing</h1>
              <p className="text-[13px] text-white/80 leading-4 truncate" data-testid="billing-page-subtitle">Generate invoices, manage billing, and track payments all in one place.</p>
            </div>
            <img
              src="/billing/hero-invoice.png"
              alt=""
              aria-hidden="true"
              draggable="false"
              className="bd-hero-art hidden lg:block absolute right-6 top-1/2 h-[112px] w-auto pointer-events-none select-none"
              data-testid="billing-hero-illustration"
            />
          </div>
          
          {/* Phase B UI follow-up (Feb 2026) — Unified, always-visible
              filter strip. Activity-only sub-controls (search box and
              status pills) are hidden in Monthly view. Client + Date
              filters apply to BOTH views. */}
          <div className="bd-enter bd-enter-1 bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(15,23,42,0.05),0_8px_24px_-20px_rgba(15,23,42,0.25)] px-4 py-3">
            {/* Row 1 — one connected filter bar reading left → right:
                Search → Client → Date Range → Status → Apply → Clear */}
            <div className="flex flex-wrap 2xl:flex-nowrap items-center gap-2" data-testid="billing-filter-row-1">
              {/* Compact "Billing" label replaces the oversized
                  Billing Dashboard sub-header (Feb 2026). */}
              <span className="sr-only" data-testid="billing-page-label">Billing</span>
              
              {/* Search Box (Activity-only) */}
              {viewMode === 'activity' && (
                <>
                  <div className="relative flex-1 min-w-[150px] max-w-[260px]">
                    {searchQuery.trim() && (searchQuery !== debouncedSearch || isLoading) ? (
                      <Loader2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 animate-spin pointer-events-none" data-testid="billing-search-spinner" />
                    ) : (
                      <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                    )}
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      placeholder="Search by file #, order #, or property address"
                      className="h-9 pl-9 pr-8 w-full min-w-[150px] text-sm border-slate-200"
                      data-testid="billing-search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => { setSearchQuery(''); setDebouncedSearch(''); setCurrentPage(1); }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        data-testid="billing-search-clear"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {debouncedSearch.trim() && !isLoading && searchQuery === debouncedSearch && (
                    <span className="text-xs font-medium text-slate-500 whitespace-nowrap" data-testid="billing-search-results-count">
                      {totalCount} result{totalCount === 1 ? '' : 's'} found for &ldquo;{debouncedSearch.trim()}&rdquo;
                    </span>
                  )}
                  <div className="h-6 w-px bg-[#E5E7EB]" />
                </>
              )}
              
              <Select value={draftClient} onValueChange={setDraftClient}>
                <SelectTrigger className="w-[164px] h-9 text-sm border-slate-200 shrink-0" data-testid="client-filter">
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clientCodes.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.code} - {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* BILLING-UNBILLED-FILTER-002: Unbilled scope — All (backlog, default) | Month | Custom range */}
              {billingStatusFilter === 'unbilled' && (
                <div className="inline-flex rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-1 h-10 items-center" data-testid="unbilled-scope-control">
                  {[
                    { value: 'all', label: 'All Backlog', icon: Layers },
                    { value: 'month', label: 'Month-wise', icon: Calendar },
                    { value: 'custom', label: 'Custom Range', icon: CalendarRange },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleUnbilledScope(opt.value)}
                      aria-pressed={draftUnbilledScope === opt.value}
                      data-testid={`unbilled-scope-${opt.value}`}
                      className={`bd-seg inline-flex items-center gap-1.5 px-2.5 h-8 text-[13px] font-semibold rounded-lg whitespace-nowrap ${
                        draftUnbilledScope === opt.value
                          ? 'bg-white text-[#3730A3] shadow-sm ring-1 ring-[#C7D2FE]'
                          : 'text-[#6B7280] hover:text-[#111827] hover:bg-white/70'
                      }`}
                    >
                      <opt.icon className="w-3.5 h-3.5" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              {/* BILLING-UI-PREMIUM-002 — fixed-width period slot: its width never changes between
                  All / Month-wise / Custom Range, so the status pills never reflow (no layout shift). */}
              <div className="flex items-center gap-2 h-10 w-[312px] shrink-0" data-testid="billing-period-slot">
              {billingStatusFilter === 'unbilled' && draftUnbilledScope === 'month' ? (
                <input
                  type="month"
                  value={draftUnbilledMonth}
                  onChange={(e) => handleUnbilledMonth(e.target.value)}
                  data-testid="unbilled-month-picker"
                  aria-label="Billing month"
                  className="h-9 w-[176px] rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30"
                />
              ) : datesInactive ? (
                <span className="text-xs text-slate-400 truncate" data-testid="period-slot-note" title={outstandingDatesNote}>
                  {isExcludedQueue ? 'All excluded orders' : 'Complete backlog'}
                </span>
              ) : (
              /* BILLING-UNBILLED-BACKLOG-001: Unbilled (All) / Excluded = outstanding queues → dates hidden (drafts preserved) */
              <div className="flex items-center gap-2" data-testid="date-range-controls" data-dates-inactive="false">
                <DateRangeField
                  label="From"
                  value={draftDateFrom}
                  onCommit={setDraftDateFrom}
                  testid="date-from-filter"
                  disabled={datesInactive}
                  disabledReason={outstandingDatesNote}
                />
                <span className="text-slate-300">—</span>
                <DateRangeField
                  label="To"
                  value={draftDateTo}
                  onCommit={setDraftDateTo}
                  testid="date-to-filter"
                  disabled={datesInactive}
                  disabledReason={outstandingDatesNote}
                />
              </div>
              )}
              </div>
              
              {/* Status filter pills (Activity-only) */}
              {viewMode === 'activity' && (
                <div className="inline-flex rounded-xl border border-[#E5E7EB] bg-[#F3F4F6] p-1 h-10 items-center ml-auto" data-testid="status-filter-pills">
                  {[
                    { value: 'unbilled', label: 'Unbilled', kpi: 'unbilled-amount', tab: 'orders' },
                    { value: 'invoiced', label: 'Invoiced', kpi: 'invoiced', tab: 'invoices' },
                    { value: 'paid', label: 'Paid', kpi: 'collected', tab: 'invoices' },
                    { value: 'excluded', label: 'Excluded', kpi: null, tab: 'orders' },
                    { value: 'all', label: 'All', kpi: null, tab: 'orders' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleStatusTab(opt)}
                      aria-pressed={billingStatusFilter === opt.value}
                      data-testid={`status-filter-${opt.value}`}
                      className={`bd-seg px-3 h-8 text-[13px] font-semibold rounded-lg ${
                        billingStatusFilter === opt.value
                          ? 'bg-[#4F46E5] text-white shadow-[0_4px_12px_-4px_rgba(79,70,229,0.7)]'
                          : 'text-[#6B7280] hover:text-[#111827] hover:bg-white/70'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5 pt-2.5 border-t border-slate-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchBillingData(true)}
                disabled={isLoading}
                className="h-8 px-2.5 text-xs text-slate-500 hover:text-slate-800"
              >
                <RefreshCw className={`w-4 h-4 mr-2 text-slate-500 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {/* Summary PDF button removed (BILLING-UI-002, Jun 2026 — user request) */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportCSV}
                disabled={isLoading}
                className="bd-chip h-9 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:border-indigo-300 hover:bg-indigo-50/60 hover:text-indigo-700"
                data-testid="header-export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2 text-indigo-600" />
                Export Invoice CSV
              </Button>
              {/* BILLING-INVOICE-XLSX-001 — in the Invoiced/Paid views this is a PERIOD
                  statement (all matching invoiced orders), not a specific invoice. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExportXLSX}
                disabled={isLoading}
                className="bd-chip h-9 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:border-emerald-300 hover:bg-emerald-50/60 hover:text-emerald-800"
                data-testid="header-export-xlsx-btn"
                title={billingStatusFilter === 'unbilled'
                  ? 'Exports the Excel billing statement for the selected filters/date range'
                  : 'Exports ALL matching invoiced orders for the selected filters/date range — to download one specific invoice, use “Download Invoice Excel” on its row'}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                {billingStatusFilter === 'unbilled' ? 'Export Invoice Excel' : 'Export Period Statement'}
              </Button>
              {/* Bulk Payment Upload — Feb 25, 2026 P0 */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBulkPaymentUpload(true)}
                className="bd-chip h-9 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:border-sky-300 hover:bg-sky-50/60 hover:text-sky-800"
                data-testid="bulk-payment-upload-cta"
              >
                <Upload className="w-4 h-4 mr-2 text-sky-600" />
                Bulk Payment Upload
              </Button>

              {/* BILLING-UI-PREMIUM-001 — Apply / Clear / period chip anchored right (reference layout) */}
              <div className="flex items-center gap-2 ml-auto">
              {/* BILLING-UNBILLED-FILTER-002 — applied period chip; × returns to All Unbilled */}
              {unbilledPeriodActive && (
                <button
                  type="button"
                  onClick={clearUnbilledPeriod}
                  data-testid="unbilled-period-chip"
                  title="Back to All Backlog (complete backlog)"
                  className="bd-chip inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-[#C7D2FE] bg-[#EEF2FF] text-[#3730A3] text-[13px] font-semibold hover:bg-[#E0E7FF]"
                >
                  Filtered: {unbilledPeriodLabel}
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {/* BILLING-FILTER-DRAFT-001 — Apply draft filters in ONE request */}
              <Button
                size="sm"
                onClick={handleApplyFilters}
                disabled={isLoading && filtersDirty}
                data-testid="apply-filters-btn"
                className={`bd-cta h-9 px-4 rounded-xl font-semibold shadow-sm text-white ${
                  filtersDirty
                    ? 'bd-cta-gradient ring-2 ring-[#4F46E5]/25 shadow-[0_8px_20px_-10px_rgba(79,70,229,0.7)]'
                    : 'bd-cta-gradient'
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Filter className="w-4 h-4 mr-1.5" />
                    Apply Filters
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                data-testid="clear-filters-btn"
                className="bd-chip h-9 px-3 rounded-xl border border-[#E5E7EB] bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                title="Reset filters to defaults"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
              </div>
            </div>
          </div>
          
          {/* Activity | Monthly toggle removed (Jun 2026 — user request).
              viewMode stays 'activity'; Monthly code path retained but dormant. */}
          {viewMode === 'monthly' ? (
            <MonthlyView
              data={monthlyData}
              isLoading={isMonthlyLoading}
              formatCurrency={formatCurrency}
            />
          ) : (
          <>
          {/* ── KPI Row — finance-grade 4-card layout (Apr 24, 2026) ─────────
              1. Total Unbilled Amount (primary tint) — $ sum, lead indicator
              2. Orders Ready for Billing (count) — formerly "Unbilled"
              3. Invoiced — $ sum + count helper
              4. Collected — $ sum + "Paid invoices" sub-label (Q3-iii)
              All 4 remain click-to-filter for the UX team.                    */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="billing-kpi-grid">
            <KPICard
              title="Total Unbilled Amount"
              subtitle={unbilledPeriodLabel ? `Filtered: ${unbilledPeriodLabel}` : undefined}
              value={kpiValue(stats.readyAmount, formatCurrency)}
              numericValue={kpiNumeric(stats.readyAmount)}
              format={formatCurrency}
              enterIndex={0}
              status={kpiStatus}
              onRetry={fetchBillingData}
              helper={unbilledPeriodLabel && stats.backlogAmount != null
                ? `${kpiValue(stats.readyCount, (n) => n.toLocaleString())} orders · of ${formatCurrency(stats.backlogAmount)} total backlog`
                : `${kpiValue(stats.readyCount, (n) => n.toLocaleString())} orders awaiting invoice`}
              icon={DollarSign}
              colorConfig={{ iconBg: '#E0E7FF', iconColor: '#4F46E5', tint: '#EEF2FF' }}
              onClick={() => {
                setActiveTab('orders');
                setBillingStatusFilter('unbilled');
                setActiveKpi('unbilled-amount');
                setCurrentPage(1);
              }}
              active={activeKpi === 'unbilled-amount'}
            />
            <KPICard
              title="Orders Ready for Billing"
              subtitle={unbilledPeriodLabel ? `Filtered: ${unbilledPeriodLabel}` : undefined}
              value={kpiValue(stats.readyCount, (n) => n.toLocaleString())}
              numericValue={kpiNumeric(stats.readyCount)}
              format={(v) => Math.round(v).toLocaleString()}
              enterIndex={1}
              status={kpiStatus}
              onRetry={fetchBillingData}
              helper={unbilledPeriodLabel && stats.backlogCount != null
                ? `${kpiValue(stats.readyAmount, formatCurrency)} · of ${stats.backlogCount.toLocaleString()} total backlog`
                : kpiValue(stats.readyAmount, formatCurrency)}
              icon={Clock}
              colorConfig={{ iconBg: '#FEF3C7', iconColor: '#B45309', tint: '#FFFBEB' }}
              onClick={() => {
                setActiveTab('orders');
                setBillingStatusFilter('unbilled');
                setActiveKpi('ready-count');
                setCurrentPage(1);
              }}
              active={activeKpi === 'ready-count'}
            />
            <KPICard
              title="Invoiced"
              value={kpiValue(stats.invoicedAmount, formatCurrency)}
              numericValue={kpiNumeric(stats.invoicedAmount)}
              format={formatCurrency}
              enterIndex={2}
              status={kpiStatus}
              onRetry={fetchBillingData}
              trend={kpiTrends.invoiced}
              helper={`${kpiValue(stats.invoicedCount, (n) => n.toLocaleString())} ${stats.invoicedCount === 1 ? 'invoice' : 'invoices'}`}
              icon={FileText}
              colorConfig={{ iconBg: '#D1FAE5', iconColor: '#047857', tint: '#ECFDF5' }}
              onClick={() => {
                setActiveTab('invoices');
                setBillingStatusFilter('invoiced');
                setActiveKpi('invoiced');
                setCurrentPage(1);
              }}
              active={activeKpi === 'invoiced'}
            />
            <KPICard
              title="Collected"
              value={kpiValue(stats.paidAmount, formatCurrency)}
              numericValue={kpiNumeric(stats.paidAmount)}
              format={formatCurrency}
              enterIndex={3}
              status={kpiStatus}
              onRetry={fetchBillingData}
              trend={kpiTrends.collected}
              helper={`${kpiValue(stats.paidCount, (n) => n.toLocaleString())} ${stats.paidCount === 1 ? 'invoice paid' : 'invoices paid'}`}
              icon={CheckCircle2}
              colorConfig={{ iconBg: '#EDE9FE', iconColor: '#6D28D9', tint: '#F5F3FF' }}
              onClick={() => {
                setActiveTab('invoices');
                setBillingStatusFilter('paid');
                setActiveKpi('collected');
                setCurrentPage(1);
              }}
              active={activeKpi === 'collected'}
            />
          </div>

          {/* Compact exception banners (Jun 2026 polish) — side-by-side on
              desktop, stacked on small screens. Same counts / Review actions. */}
          {(deliveryExceptionCount > 0 || missingPricingCount > 0 || qcGatedCount.total > 0
            || exceptionCounts.qc.error || exceptionCounts.delivery.error || exceptionCounts.missing.error) && (
            <div className="bd-enter bd-enter-5 flex flex-col md:flex-row gap-2.5" data-testid="billing-exception-banners">
              {(qcGatedCount.total > 0 || exceptionCounts.qc.error) && (
                <div
                  className="bd-card flex-1 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-white pl-3.5 pr-2.5 py-2 flex items-center justify-between gap-3 cursor-pointer hover:border-orange-300 hover:shadow-[0_10px_24px_-18px_rgba(194,65,12,0.5)]"
                  onClick={() => setActiveKpi('qc-gated')}
                  data-testid="billing-kpi-qc-gated"
                  title="Completed orders whose QC cycle was never closed — excluded from billing until released or resolved. Click to review."
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-4 h-4 text-orange-600" /></span>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-orange-900 block truncate" data-testid="qc-gated-count" data-count-state={exceptionCounts.qc.error ? 'error' : 'ok'}>
                        <strong className="font-bold tabular-nums">{exceptionCounts.qc.value == null ? '—' : qcGatedCount.total}</strong> Completed but QC-Gated
                        {exceptionCounts.qc.value != null && <span className="text-orange-700/80 font-normal"> · ${Number(qcGatedCount.dollars || 0).toFixed(2)}{qcGatedCount.held ? ` · ${qcGatedCount.held} on hold` : ''}</span>}
                      </span>
                      <span className="text-xs text-orange-800/80 block">{exceptionCounts.qc.error ? `Count unavailable (${exceptionCounts.qc.error})${exceptionCounts.qc.value != null ? ' — showing last known value' : ''}.` : 'Completed orders whose QC cycle was never closed — excluded from billing.'}</span>
                    </div>
                  </div>
                  {exceptionCounts.qc.error && <CountRetryButton onClick={() => fetchExceptionCounts('qc')} refreshing={exceptionCounts.qc.refreshing} testid="qc-gated-count-retry" tone="orange" />}
                  <Button variant="ghost" size="sm" className="bd-chip h-8 px-3 text-xs font-semibold text-orange-800 hover:bg-orange-100 shrink-0 rounded-lg" data-testid="qc-gated-card-cta">
                    Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              )}
              {(deliveryExceptionCount > 0 || exceptionCounts.delivery.error) && (
                <div
                  className="bd-card flex-1 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white pl-3.5 pr-2.5 py-2 flex items-center justify-between gap-3 cursor-pointer hover:border-amber-300 hover:shadow-[0_10px_24px_-18px_rgba(180,83,9,0.5)]"
                  onClick={() => setActiveKpi('delivery-exceptions')}
                  data-testid="delivery-exceptions-card"
                  title="Excluded from billing until delivery is finalized. Click to review."
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-4 h-4 text-amber-600" /></span>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-amber-900 block truncate" data-testid="delivery-exceptions-count" data-count-state={exceptionCounts.delivery.error ? 'error' : 'ok'}>
                        <strong className="font-bold tabular-nums">{exceptionCounts.delivery.value == null ? '—' : deliveryExceptionCount}</strong> Completed, Not Delivered
                      </span>
                      <span className="text-xs text-amber-800/80 block">{exceptionCounts.delivery.error ? `Count unavailable (${exceptionCounts.delivery.error})${exceptionCounts.delivery.value != null ? ' — showing last known value' : ''}.` : 'These orders are completed but not marked as delivered.'}</span>
                    </div>
                  </div>
                  {exceptionCounts.delivery.error && <CountRetryButton onClick={() => fetchExceptionCounts('delivery')} refreshing={exceptionCounts.delivery.refreshing} testid="delivery-exceptions-count-retry" tone="amber" />}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="bd-chip h-8 px-3 text-xs font-semibold text-amber-800 hover:bg-amber-100 shrink-0 rounded-lg"
                    data-testid="delivery-exceptions-card-cta"
                  >
                    Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              )}
              {/* BILLING-DNB-001 cleanup: "Unclassified Billing Status" banner removed from the dashboard (count still returned by /billing/summary for diagnostics) */}
              {(missingPricingCount > 0 || exceptionCounts.missing.error) && (
                <div
                  className="bd-card flex-1 rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-white pl-3.5 pr-2.5 py-2 flex items-center justify-between gap-3 cursor-pointer hover:border-red-300 hover:shadow-[0_10px_24px_-18px_rgba(185,28,28,0.5)]"
                  onClick={() => setActiveKpi('missing-pricing')}
                  data-testid="missing-pricing-card"
                  title="Cannot be invoiced. Configure a client pricing rule or product default price."
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-4 h-4 text-red-600" /></span>
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-red-900 block truncate" data-testid="missing-pricing-count" data-count-state={exceptionCounts.missing.error ? 'error' : 'ok'}>
                        <strong className="font-bold tabular-nums">{exceptionCounts.missing.value == null ? '—' : missingPricingCount}</strong> Missing Pricing
                      </span>
                      <span className="text-xs text-red-800/80 block">{exceptionCounts.missing.error ? `Count unavailable (${exceptionCounts.missing.error})${exceptionCounts.missing.value != null ? ' — showing last known value' : ''}.` : 'These orders are missing product pricing.'}</span>
                    </div>
                  </div>
                  {exceptionCounts.missing.error && <CountRetryButton onClick={() => fetchExceptionCounts('missing')} refreshing={exceptionCounts.missing.refreshing} testid="missing-pricing-count-retry" tone="red" />}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="bd-chip h-8 px-3 text-xs font-semibold text-red-800 hover:bg-red-100 shrink-0 rounded-lg"
                    data-testid="missing-pricing-card-cta"
                  >
                    Review <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Filter strip moved to a shared, always-visible position
              ABOVE the View Toggle (Phase B UI follow-up, Feb 2026).
              Activity-only sub-controls (search box, status pills) are
              hidden when viewMode === 'monthly'. */}
          
          {/* Slim Contextual Selection Toolbar
              Apr 26, 2026 — Appears below the filter strip ONLY when rows are
              selected. Replaces the inline "Generate Invoice" / "Mark Selected
              as Paid" buttons that previously lived in the orders/invoices
              section headers, eliminating duplication and following
              Linear/Stripe-style bulk-action patterns. */}
          {(() => {
            // BILLING-UI-SELBAR-001 (Sep 2026): the dark "Orders selected" bar was removed —
            // Generate Invoice (n) + Clear now live in the Orders Ready for Billing header.
            const showOrdersToolbar = false;
            const showInvoicesToolbar = activeTab === 'invoices' && selectedInvoiceIds.size > 0;
            if (!showOrdersToolbar && !showInvoicesToolbar) return null;
            
            const count = showOrdersToolbar ? selectedOrderIds.size : selectedInvoiceIds.size;
            const total = showOrdersToolbar ? selectedTotal : selectedInvoicesTotal;
            
            // By-client preview (Apr 26, 2026) — orders only.
            // Lets accounting sanity-check invoice splits BEFORE generating.
            // Each unique client_code → its own invoice, so this surfaces
            // accidental cross-client merges before the click.
            const clientChips = showOrdersToolbar
              ? Object.entries(selectedByClient)
                  .sort((a, b) => b[1].total - a[1].total)
              : [];
            const isMultiClient = clientChips.length > 1;
            const visibleChips = clientChips.slice(0, 3);
            const overflowCount = Math.max(0, clientChips.length - visibleChips.length);
            
            return (
              <div
                className="sticky top-2 z-20 mb-3 bg-slate-900 text-white rounded-lg shadow-lg border border-slate-800/60 px-4 py-2.5 flex items-center justify-between gap-4"
                data-testid="selection-toolbar"
              >
                {/* Left: selection summary */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-md bg-[#4F46E5] text-white text-xs font-bold tabular-nums">
                    {count}
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {showOrdersToolbar ? 'Orders selected' : 'Invoices selected'}
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-white">
                      {formatCurrency(total)}
                    </span>
                  </div>
                  <button
                    onClick={() => showOrdersToolbar ? setSelectedOrderIds(new Set()) : setSelectedInvoiceIds(new Set())}
                    className="ml-2 px-2 h-7 inline-flex items-center gap-1 rounded-md text-xs text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                    data-testid={showOrdersToolbar ? 'clear-selection-btn' : 'clear-invoice-selection-btn'}
                    title="Clear selection"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                </div>
                
                {/* Middle: by-client preview (orders only)
                    Apr 26, 2026 — Each unique client = its own invoice.
                    Surfacing the split here prevents accidental
                    cross-client merges before the user clicks Generate. */}
                {showOrdersToolbar && clientChips.length > 0 && (
                  <div
                    className="hidden lg:flex items-center gap-2 min-w-0 flex-1 px-4 border-l border-r border-slate-800"
                    data-testid="selection-by-client-preview"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">
                      {clientChips.length} {clientChips.length === 1 ? 'client' : 'clients'}
                    </span>
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      {visibleChips.map(([clientCode, group]) => (
                        <span
                          key={clientCode}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium tabular-nums whitespace-nowrap ${
                            isMultiClient
                              ? 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/30'
                              : 'bg-slate-800 text-slate-200 ring-1 ring-slate-700'
                          }`}
                          title={`${group.orders.length} order(s) — ${formatCurrency(group.total)}`}
                        >
                          <span className="font-semibold">{clientCode}</span>
                          <span className="text-slate-400">·</span>
                          <span>{formatCurrency(group.total)}</span>
                        </span>
                      ))}
                      {overflowCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-800 text-slate-300 ring-1 ring-slate-700 whitespace-nowrap">
                          +{overflowCount} more
                        </span>
                      )}
                    </div>
                    {isMultiClient && (
                      <span
                        className="text-[10px] text-amber-300/90 whitespace-nowrap"
                        title="Each unique client will receive its own invoice"
                      >
                        → {clientChips.length} invoices
                      </span>
                    )}
                  </div>
                )}
                
                {/* Right: contextual action button */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {showOrdersToolbar && (
                    <Button
                      onClick={openInvoicePreview}
                      className="h-9 px-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white shadow-sm transition-all"
                      data-testid="generate-invoice-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Invoice ({count})
                    </Button>
                  )}                  {showInvoicesToolbar && (
                    <Button
                      onClick={() => setShowBulkPayConfirm(true)}
                      className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all"
                      data-testid="bulk-mark-paid-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Mark {count} as Paid
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* BILLING-BULK-001 (Feb 2026) — "Select all matching orders" banner.
              Surfaces when the user has selected every eligible row on the
              current page AND there are more matching unbilled orders for the
              active client+period filter beyond this page. Lets the user
              invoice the entire filtered set in one go via the dry-run /
              confirm flow on POST /billing/invoices/generate-by-filter. */}
          {showBulkAllMatchingBanner && (
            <div
              className="mb-3 bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-lg px-4 py-2.5 flex items-center justify-between gap-4"
              data-testid="bulk-all-matching-banner"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Sparkles className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <div className="text-sm leading-tight">
                  <span className="font-semibold">All {pageEligibleOrders.length} orders on this page are selected.</span>
                  <span className="ml-1 text-indigo-700">
                    {/* BILLING-PARITY-001 — show the invoice-ELIGIBLE count
                        (kpis.unbilled, which now mirrors the bulk preview
                        exactly) instead of the raw table total that also
                        counts missing-pricing / QC-pending rows. */}
                    {(stats.readyCount || totalCount).toLocaleString()} matching unbilled orders exist for this client &amp; period.
                  </span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={openBulkInvoicePreview}
                disabled={isLoadingBulkPreview}
                className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-sm"
                data-testid="invoice-all-matching-btn"
              >
                {isLoadingBulkPreview ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>Invoice all {(stats.readyCount || totalCount).toLocaleString()} matching orders →</>
                )}
              </Button>
            </div>
          )}

          {/* Tabs & Content
              Apr 25, 2026 — KPI cards + status pills are the canonical
              navigation. The visible <TabsList> was removed because it
              duplicated the Unbilled/Invoiced filter behaviour. The Tabs
              wrapper itself stays so `activeTab` keeps driving which
              <TabsContent> renders, but it's now a pure state container,
              not a UI surface. */}
          <Tabs
            value={activeTab}
            onValueChange={(val) => {
              setActiveTab(val);
              if (val === 'orders') {
                if (activeKpi !== 'unbilled-amount' && activeKpi !== 'ready-count') {
                  setActiveKpi('ready-count');
                  setBillingStatusFilter('unbilled');
                }
              } else if (val === 'invoices') {
                if (activeKpi !== 'invoiced' && activeKpi !== 'collected') {
                  setActiveKpi('invoiced');
                  setBillingStatusFilter('invoiced');
                }
              }
              setCurrentPage(1);
            }}
            className="space-y-2"
          >
            {/* View selector (External / Internal / Profit) REMOVED — Jun 2026
                user request. billingView stays 'external' (default behavior);
                internal/profit render paths retained but dormant. */}
            
            {/* Delivery Exceptions queue — Feb 25, 2026 P0.
                Renders when the user clicks the "Completed but Not Delivered" card.
                Shows a focused, read-mostly list of orders that are status=complete
                but delivery_status is not finalized. */}
            {activeKpi === 'qc-gated' && (
              <QcGatedQueuePanel
                onClose={() => setActiveKpi('unbilled-amount')}
                onChanged={() => { fetchQcGatedCount(); fetchBillingData(); }}
              />
            )}
            {activeKpi === 'delivery-exceptions' && (
              <div
                className="bg-white rounded-xl border border-amber-200 overflow-hidden mb-4"
                data-testid="delivery-exceptions-queue"
              >
                <div className="px-6 py-3 border-b border-slate-100 bg-amber-50/40 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Completed but Not Delivered
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      These orders are excluded from billing. Open each to mark delivery complete or follow up with the assignee.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveKpi('unbilled-amount')}
                    className="text-slate-600"
                    data-testid="delivery-exceptions-close"
                  >
                    Close
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  {deliveryExceptionsLoading ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-500">Loading…</div>
                  ) : deliveryExceptions.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-500">
                      No orders in this state. Nice — everything is delivered.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="tf-thead text-xs">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Order #</th>
                          <th className="text-left px-4 py-2 font-medium">Client</th>
                          <th className="text-left px-4 py-2 font-medium">Product</th>
                          <th className="text-left px-4 py-2 font-medium">Delivery Status</th>
                          <th className="text-left px-4 py-2 font-medium">Method</th>
                          <th className="text-left px-4 py-2 font-medium">Assignee</th>
                          <th className="text-left px-4 py-2 font-medium">Hours Overdue</th>
                          <th className="text-left px-4 py-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveryExceptions.map((o) => (
                          <tr
                            key={o.id}
                            className="border-t border-slate-100 hover:bg-amber-50/30"
                            data-testid={`delivery-exception-row-${o.id}`}
                          >
                            <td className="px-4 py-2 font-mono font-semibold text-slate-900">{o.order_number}</td>
                            <td className="px-4 py-2 text-slate-700">{o.client_code || '—'}</td>
                            <td className="px-4 py-2 text-slate-700">{o.client_product || '—'}</td>
                            <td className="px-4 py-2">
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                                {o.delivery_status || 'unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-600 text-xs">{o.delivery_method || '—'}</td>
                            <td className="px-4 py-2 text-slate-600 text-xs">{o.assignee_name || '—'}</td>
                            <td className="px-4 py-2 text-slate-700">
                              {typeof o.hours_since_completion === 'number'
                                ? `${o.hours_since_completion}h`
                                : '—'}
                            </td>
                            <td className="px-4 py-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/orders/${o.id}/overview?from=billing`)}
                                className="h-7 px-2 text-xs font-medium text-[#4338CA] hover:text-[#3730A3] hover:bg-indigo-50"
                                data-testid={`delivery-exception-review-${o.id}`}
                              >
                                Review
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Missing Pricing focused queue — Feb 2026 (Billing Governance).
                Renders when the user clicks the "Missing Pricing" card.
                Shows the focused list of $0.00 orders that are blocked
                from invoice generation until pricing is configured. */}
            {activeKpi === 'missing-pricing' && (
              <div
                className="bg-white rounded-xl border border-red-200 overflow-hidden mb-4"
                data-testid="missing-pricing-queue"
              >
                <div className="px-6 py-3 border-b border-slate-100 bg-red-50/40 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Missing Pricing — Billing Exceptions
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      These orders have no client pricing rule and no product default price. Invoice generation is blocked until pricing is configured. Open each order to set a manual override, or configure pricing in Admin → Client Pricing or Product Management.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleRecalculateAllMissingPricing}
                      disabled={isRecalculatingAll || missingPricingOrders.length === 0}
                      className="h-8 px-3 text-xs font-medium bg-[#4F46E5] hover:bg-[#4338CA] text-white disabled:bg-slate-200 disabled:text-slate-400"
                      data-testid="recalculate-all-missing-pricing-btn"
                      title="Sweep all missing-pricing orders through the pricing engine. Safe — skips invoiced/locked/overridden orders."
                    >
                      {isRecalculatingAll ? 'Recalculating…' : 'Recalculate All'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveKpi('unbilled-amount')}
                      className="text-slate-600"
                      data-testid="missing-pricing-close"
                    >
                      Close
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {missingPricingLoading ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-500">Loading…</div>
                  ) : missingPricingOrders.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-500">
                      No orders in this state. Pricing governance is clear.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="tf-thead text-xs">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Order #</th>
                          <th className="text-left px-4 py-2 font-medium">Client</th>
                          <th className="text-left px-4 py-2 font-medium">Product</th>
                          <th className="text-left px-4 py-2 font-medium">Property</th>
                          <th className="text-right px-4 py-2 font-medium">Amount</th>
                          <th className="text-left px-4 py-2 font-medium">Completed</th>
                          <th className="text-left px-4 py-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingPricingOrders.map((o) => (
                          <tr
                            key={o.id}
                            className="border-t border-slate-100 hover:bg-red-50/30"
                            data-testid={`missing-pricing-row-${o.id}`}
                          >
                            <td className="px-4 py-2 font-mono font-semibold text-slate-900">{o.order_number}</td>
                            <td className="px-4 py-2 text-slate-700">{o.client_code || '—'}</td>
                            <td className="px-4 py-2 text-slate-700">{o.client_product || '—'}</td>
                            <td className="px-4 py-2 text-slate-600 text-xs truncate max-w-[220px]" title={o.property_address_display || o.property_address_full || o.property_address || ''}>
                              {formatStreetAddress(o.property_address_display || o.property_address_full || o.property_address) || '—'}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white">
                                <AlertTriangle className="w-3 h-3" />
                                {formatCurrency(0)}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-600 text-xs">{formatDate(o.completed_at || o.created_at)}</td>
                            <td className="px-4 py-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/orders/${o.id}/overview?from=billing`)}
                                className="h-7 px-2 text-xs font-medium text-[#4338CA] hover:text-[#3730A3] hover:bg-indigo-50"
                                data-testid={`missing-pricing-review-${o.id}`}
                              >
                                Configure
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            <TabsContent value="orders" className="mt-0">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(15,23,42,0.05),0_12px_32px_-24px_rgba(15,23,42,0.3)] overflow-hidden" data-testid="orders-table-card">
                {/* Compact section header (Apr 25, 2026)
                    — title + subtitle merged inline, py-4 → py-2.5,
                      drops ~24px of vertical real estate. */}
                <div className="px-5 py-2.5 border-b border-slate-100 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><FileText className="w-[18px] h-[18px]" /></span>
                    <div className="min-w-0">
                    <h3 className="text-base font-semibold text-slate-900 whitespace-nowrap leading-5">
                      {billingView === 'external'
                        ? isExcludedQueue ? 'Excluded from Billing' : 'Orders Ready for Billing'
                        : billingView === 'internal' ? 'Internal Cost Tracking' : 'Profitability Analysis'}
                    </h3>
                    <span className="text-xs text-slate-500 block truncate">
                      {billingView === 'external'
                        ? billingStatusFilter === 'unbilled' ? 'Select orders to generate invoices. Only eligible orders are shown.'
                          : billingStatusFilter === 'invoiced' ? 'Orders already invoiced'
                          : billingStatusFilter === 'paid' ? 'Orders already paid'
                          : billingStatusFilter === 'excluded' ? 'Orders currently excluded from billing. Include an order to return it to the Unbilled queue.'
                          : 'All orders across all statuses'
                        : billingView === 'internal' ? 'Cost per order for ops tracking' : 'Revenue, cost & margin'}
                    </span>
                    </div>
                  </div>
                  {/* Right side: page-total summary
                      Apr 26, 2026 — Selection metrics moved to the slim
                      contextual toolbar above. This block now strictly
                      reflects the *page* total, regardless of selection. */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {billingView === 'external' && sortedBillableOrders.length > 0 && (
                      <div
                        className="hidden md:flex flex-col items-end text-right"
                        data-testid="page-total-summary"
                      >
                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Page Total</span>
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(pageTotalAmount)}</span>
                      </div>
                    )}
                    {/* BILLING-UI-SELBAR-001: inline selection actions (replaces the dark selection bar) */}
                    {billingView === 'external' && billingStatusFilter === 'unbilled' && (
                      <div className="flex items-center gap-2" data-testid="orders-selection-actions">
                        {selectedOrderIds.size > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedOrderIds(new Set())}
                            className="bd-chip h-8 px-2.5 inline-flex items-center gap-1 rounded-lg text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                            data-testid="clear-selection-btn"
                            title="Clear selection"
                          >
                            <X className="w-3.5 h-3.5" />
                            Clear
                          </button>
                        )}
                        <div className="flex flex-col items-end">
                          <Button
                            onClick={openInvoicePreview}
                            disabled={selectedOrderIds.size === 0}
                            className={`bd-cta h-10 px-4 rounded-xl text-white font-semibold ${selectedOrderIds.size > 0
                              ? 'bd-cta-gradient shadow-[0_10px_24px_-10px_rgba(79,70,229,0.75)]'
                              : 'bg-[#A5B4FC] shadow-none cursor-not-allowed'} disabled:opacity-100`}
                            data-testid="generate-invoice-btn"
                            title={selectedOrderIds.size === 0 ? 'Select eligible orders to generate an invoice' : `${formatCurrency(selectedTotal)} across ${Object.keys(selectedByClient).length} client(s)`}
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Invoice{selectedOrderIds.size > 0 ? ` (${selectedOrderIds.size})` : ''}
                          </Button>
                          <span className={`text-[11px] mt-1 transition-opacity duration-300 ${selectedOrderIds.size === 0 ? 'text-slate-400 opacity-100' : 'text-indigo-600 opacity-100'}`} data-testid="generate-invoice-hint">
                            {selectedOrderIds.size === 0 ? 'Select one or more orders to enable' : `${formatCurrency(selectedTotal)} selected`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Jun 2026 — no horizontal scroll: table flexes to fit the
                    viewport (row actions were slimmed down to Exclude only). */}
                {ordersError && !isLoading && (
                  <PanelError message={`Orders could not be loaded (${ordersError}).`} onRetry={fetchBillingData} testid="orders-panel-error" />
                )}
                <div className={`transition-opacity duration-200 ${isLoading && !staleData ? 'opacity-60' : 'opacity-100'}`} data-testid="orders-table-wrap" data-stale={staleData ? 'true' : 'false'}>
                  {/* BILLING-EXCLUDED-TAB-001: dedicated Excluded queue table */}
                  {billingView === 'external' && isExcludedQueue ? (
                    <ExcludedOrdersTable
                      orders={staleData ? EMPTY_ROWS : billableOrders}
                      loading={staleData || (isLoading && billableOrders.length === 0)}
                      onIncluded={() => fetchBillingData()}
                    />
                  ) : (
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bd-thead-navy border-b border-[#1E3A8A]">
                        {/* Checkbox column — External view, unbilled filter only */}
                        {billingView === 'external' && billingStatusFilter === 'unbilled' && (
                          <th className="px-2.5 py-2 w-9">
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.size > 0 && selectedOrderIds.size === sortedBillableOrders.filter(o => !o.invoice_id && (o.billing_status === 'unbilled' || !o.billing_status) && !o.is_missing_pricing).length}
                              onChange={toggleSelectAll}
                              className="bd-check !border-white/70 !bg-white/10 checked:!bg-white checked:!border-white [&:checked::after]:!border-[#1E3A8A]"
                              data-testid="select-all-checkbox"
                            />
                          </th>
                        )}
                        {/* Common columns: Product ID, Client, Product, Completed */}
                        <th 
                          className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('order_number') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleOrdersSort('order_number')}
                        >
                          <span className="flex items-center gap-1">
                            Product ID
                            {getOrdersSortDirection('order_number') && <span>{getOrdersSortDirection('order_number') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('client_code') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleOrdersSort('client_code')}
                        >
                          <span className="flex items-center gap-1">
                            Client
                            {getOrdersSortDirection('client_code') && <span>{getOrdersSortDirection('client_code') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        
                        {billingView === 'profit' ? (
                          <>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide">Product</th>
                            <th className="px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide">Property Address</th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold text-white uppercase tracking-wide">Revenue</th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold text-white uppercase tracking-wide">Cost</th>
                            <th className="px-3 py-2 text-right text-[11px] font-semibold text-white uppercase tracking-wide">Profit</th>
                            <th className="px-3 py-2 text-center text-[11px] font-semibold text-white uppercase tracking-wide">Margin</th>
                          </>
                        ) : (
                          <>
                            <th 
                              className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('client_product') ? 'bg-white/15 text-white' : ''}`}
                              onClick={() => handleOrdersSort('client_product')}
                            >
                              <span className="flex items-center gap-1">
                                Product
                                {getOrdersSortDirection('client_product') && <span>{getOrdersSortDirection('client_product') === 'asc' ? '↑' : '↓'}</span>}
                              </span>
                            </th>
                            <th 
                              className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('property_address') ? 'bg-white/15 text-white' : ''}`}
                              onClick={() => handleOrdersSort('property_address')}
                            >
                              <span className="flex items-center gap-1">
                                Property Address
                                {getOrdersSortDirection('property_address') && <span>{getOrdersSortDirection('property_address') === 'asc' ? '↑' : '↓'}</span>}
                              </span>
                            </th>
                            <th 
                              className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('completed_at') ? 'bg-white/15 text-white' : ''}`}
                              onClick={() => handleOrdersSort('completed_at')}
                            >
                              <span className="flex items-center gap-1">
                                Completed
                                {getOrdersSortDirection('completed_at') && <span>{getOrdersSortDirection('completed_at') === 'asc' ? '↑' : '↓'}</span>}
                              </span>
                            </th>
                            <th 
                              className={`px-3 py-2 text-right text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection(billingView === 'internal' ? 'internal_unit_price_snapshot' : 'total_billable_amount') ? 'bg-white/15 text-white' : ''}`}
                              onClick={() => handleOrdersSort(billingView === 'internal' ? 'internal_unit_price_snapshot' : 'total_billable_amount')}
                            >
                              <span className="flex items-center justify-end gap-1">
                                {billingView === 'internal' ? 'Cost' : 'Billing'}
                                {getOrdersSortDirection(billingView === 'internal' ? 'internal_unit_price_snapshot' : 'total_billable_amount') && <span>{getOrdersSortDirection(billingView === 'internal' ? 'internal_unit_price_snapshot' : 'total_billable_amount') === 'asc' ? '↑' : '↓'}</span>}
                              </span>
                            </th>
                            <th 
                              className={`px-3 py-2 text-center text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('billing_status') ? 'bg-white/15 text-white' : ''}`}
                              onClick={() => handleOrdersSort('billing_status')}
                            >
                              <span className="flex items-center justify-center gap-1">
                                Status
                                {getOrdersSortDirection('billing_status') && <span>{getOrdersSortDirection('billing_status') === 'asc' ? '↑' : '↓'}</span>}
                              </span>
                            </th>
                            {billingView === 'external' && (
                              <>
                                <th className="px-3 py-2 text-center text-[11px] font-semibold text-white uppercase tracking-wide">Audit</th>
                                {/* BILLING-UNBILLED-INVOICE-COL-001: Unbilled rows are never invoiced → no Invoice column there; kept for All */}
                                {showInvoiceColumn && (
                                <th 
                                  className={`px-3 py-2 text-left text-[11px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getOrdersSortDirection('invoice_number') ? 'bg-white/15 text-white' : ''}`}
                                  onClick={() => handleOrdersSort('invoice_number')}
                                  data-testid="orders-invoice-column-header"
                                >
                                  <span className="flex items-center gap-1">
                                    Invoice
                                    {getOrdersSortDirection('invoice_number') && <span>{getOrdersSortDirection('invoice_number') === 'asc' ? '↑' : '↓'}</span>}
                                  </span>
                                </th>
                                )}
                                <th className="px-3 py-2 text-center text-[11px] font-semibold text-white uppercase tracking-wide">Actions</th>
                              </>
                            )}
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className={`transition-opacity duration-200 ${ordersHoldPrev ? 'opacity-40 pointer-events-none select-none' : 'opacity-100'}`} aria-busy={staleData ? 'true' : 'false'} data-stale={staleData ? 'true' : 'false'}>
                      {((staleData && !ordersHoldPrev) || (isLoading && !staleData && sortedBillableOrders.length === 0)) ? (
                        [0, 1, 2, 3].map((i) => (
                          <tr key={`sk-${i}`} className="border-b border-slate-100" data-testid="billing-skeleton-row">
                            {Array.from({ length: ordersColCount }).map((_, j) => (
                              <td key={j} className="px-4 py-3.5"><div className="bd-skeleton h-3.5" style={{ width: `${55 + ((i * 7 + j * 13) % 40)}%` }} /></td>
                            ))}
                          </tr>
                        ))
                      ) : ordersDisplayRows.length === 0 ? (
                        <tr>
                          <td colSpan={ordersColCount} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                <FileText className="w-6 h-6 text-slate-400" />
                              </div>
                              <p className="text-slate-500 font-medium">No billable orders for selected period</p>
                              <p className="text-sm text-slate-400 mt-1">Try adjusting your date range or filters</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        ordersDisplayRows.map((order, idx) => {
                          const extPrice = order.billing_override_price ?? order.billing_unit_price_snapshot ?? 0;
                          const intCost = order.internal_override_price ?? order.internal_unit_price_snapshot ?? 0;
                          const additionalTotal = order.additional_charges_total || 0;
                          const totalBillable = order.total_billable_amount || extPrice;
                          const profit = totalBillable - intCost;
                          const margin = totalBillable > 0 ? (profit / totalBillable) * 100 : 0;
                          const isAuditRow = order.audit_required && order.audit_status !== 'completed';
                          // Finance-grade high-amount highlight (Q1-i, Apr 24, 2026):
                          // Accent rows whose billable amount sits in the top 10% of the
                          // currently-visible page. Audit rows already own the red accent,
                          // so we only layer indigo when the row isn't an audit row and
                          // we're in the billing-focused External view.
                          const isHighAmount =
                            billingView === 'external' &&
                            !isAuditRow &&
                            totalBillable >= highAmountThreshold;
                          
                          return (
                            <tr
                              key={order.id}
                              data-testid={`billing-row-${order.id}`}
                              className={`bd-row border-b border-slate-100 last:border-0 hover:bg-[#F8FAFC] ${selectedOrderIds.has(order.id) ? 'bd-row-selected' : ''} ${
                                isAuditRow
                                  ? 'bg-red-50/40 hover:bg-red-50/60 border-l-2 border-l-red-400'
                                  : isHighAmount
                                    ? 'bg-white border-l-2 border-l-indigo-400'
                                    : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'
                              }`}
                            >
                              {/* Checkbox — External view, unbilled filter only.
                                  Missing-pricing orders are never selectable
                                  (backend invoice endpoint hard-blocks them). */}
                              {billingView === 'external' && billingStatusFilter === 'unbilled' && (
                                <td className="px-2.5 py-2 w-9">
                                  {!order.invoice_id && (order.billing_status === 'unbilled' || !order.billing_status) && !order.is_missing_pricing ? (
                                    <input
                                      type="checkbox"
                                      checked={selectedOrderIds.has(order.id)}
                                      onChange={() => toggleOrderSelection(order.id)}
                                      className="bd-check"
                                      data-testid={`select-order-${order.id}`}
                                    />
                                  ) : order.is_missing_pricing ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="w-4 h-4 rounded border border-red-300 bg-red-50 inline-flex items-center justify-center cursor-not-allowed"
                                          data-testid={`select-order-${order.id}-disabled`}
                                        >
                                          <AlertTriangle className="w-2.5 h-2.5 text-red-500" />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-[260px]">
                                        <p className="text-xs">
                                          Missing pricing — invoice generation is blocked. Configure a Client Pricing rule or set a Product Default Price first.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <span className="w-4 h-4 block" />
                                  )}
                                </td>
                              )}
                              {/* Common: Product ID — clickable, opens the billing
                                  review page (Jun 2026: replaces the removed
                                  row-level "Review" action). */}
                              <td className="px-3 py-2">
                                <div className="flex flex-col">
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/orders/${order.id}/overview?from=billing`)}
                                    className="text-sm font-medium text-[#4338CA] hover:text-[#3730A3] hover:underline tabular-nums text-left w-fit"
                                    data-testid={`billing-order-link-${order.id}`}
                                    title="Open billing review"
                                  >
                                    {order.product_id || order.order_number}
                                  </button>
                                  {order.billing_note && (
                                    <span className="text-[11px] text-amber-600 mt-0.5 flex items-center gap-1 max-w-[210px]" title={order.billing_note}>
                                      <span className="w-1 h-1 bg-amber-500 rounded-full shrink-0" />
                                      <span className="truncate">{order.billing_note}</span>
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* Common: Client */}
                              <td className="px-3 py-2">
                                <span className="text-sm font-medium text-[#374151]">{order.client_code}</span>
                              </td>
                              
                              {billingView === 'profit' ? (
                                <>
                                  <td className="px-3 py-2">
                                    <span className="text-sm text-[#374151]">{order.client_product}</span>
                                  </td>
                                  <td className="px-3 py-2" data-testid={`property-address-profit-${order.id}`}>
                                    <span className="text-sm text-[#374151] truncate block max-w-[200px]" title={order.property_address_display || order.property_address_full || order.property_address || ''}>
                                      {formatStreetAddress(order.property_address_display || order.property_address_full || order.property_address)}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <span className="font-semibold text-emerald-600 tabular-nums">
                                      {formatCurrency(totalBillable)}
                                    </span>
                                    {additionalTotal > 0 && (
                                      <span className="block text-[10px] text-slate-400">incl. +{formatCurrency(additionalTotal)}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <span className="font-semibold text-slate-600 tabular-nums">
                                      {intCost > 0 ? formatCurrency(intCost) : <span className="text-slate-400 text-xs">Not Set</span>}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <span className={`font-semibold tabular-nums ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {intCost > 0 ? formatCurrency(profit) : <span className="text-slate-400 text-xs">—</span>}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {intCost > 0 ? <MarginBadge margin={margin} /> : <span className="text-xs text-slate-400">—</span>}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-3 py-2">
                                    <span className="text-sm text-[#374151]">{order.client_product}</span>
                                  </td>
                                  <td className="px-3 py-2" data-testid={`property-address-${order.id}`}>
                                    <span className="text-sm text-[#374151] truncate block max-w-[200px]" title={order.property_address_display || order.property_address_full || order.property_address || ''}>
                                      {formatStreetAddress(order.property_address_display || order.property_address_full || order.property_address)}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="text-[13px] text-[#6B7280] whitespace-nowrap">{formatDate(order.completed_at || order.created_at)}</span>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {billingView === 'internal' ? (
                                      <>
                                        <span className="font-semibold text-slate-700 tabular-nums">
                                          {intCost > 0 ? formatCurrency(intCost) : <span className="text-slate-400 font-normal text-xs">Not Set</span>}
                                        </span>
                                        {order.internal_override_price && (
                                          <span className="text-xs text-slate-400 ml-1">(ovr)</span>
                                        )}
                                      </>
                                    ) : (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="text-right cursor-help" data-testid={`billing-breakdown-${order.id}`}>
                                            <span className="text-sm font-semibold text-[#111827] tabular-nums">
                                              {formatCurrency(totalBillable)}
                                            </span>
                                            {order.billing_override_price && (
                                              <span className="text-xs text-slate-400 ml-1">(ovr)</span>
                                            )}
                                            {/* Per-parcel billing badge — May 2, 2026 */}
                                            {(order.parcel_count || order.billing_quantity || 1) > 1 && (
                                              <div
                                                className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200"
                                                data-testid={`parcel-count-badge-${order.id}`}
                                                title={`${order.parcel_count} parcels × ${formatCurrency(order.billing_unit_rate || order.billing_unit_price_snapshot || 0)}`}
                                              >
                                                {order.parcel_count || order.billing_quantity} Parcels
                                              </div>
                                            )}
                                            {additionalTotal > 0 && (
                                              <div className="mt-0.5 space-y-0">
                                                <span className="block text-[10px] text-[#9CA3AF] tabular-nums">
                                                  Base: {formatCurrency(extPrice)}
                                                </span>
                                                <span className="block text-[10px] text-[#92400E] font-medium tabular-nums">
                                                  + Addl: {formatCurrency(additionalTotal)}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="p-3">
                                          <ChargeBreakdownContent order={order} formatCurrency={formatCurrency} />
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex flex-col items-center gap-1">
                                      <StatusBadge status={order.billing_status || 'unbilled'} />
                                      {order.is_missing_pricing && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span
                                              data-testid={`missing-pricing-badge-${order.id}`}
                                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white cursor-default"
                                            >
                                              <AlertTriangle className="w-3 h-3" />
                                              Missing Pricing
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="left" className="max-w-[260px]">
                                            <p className="text-xs">
                                              No client pricing rule and no product Default Price configured. Cannot be invoiced until pricing is set.
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </td>
                                  {billingView === 'external' && (
                                    <>
                                      <td className="px-3 py-2 text-center">
                                        <div className="flex flex-col items-center gap-1">
                                          <AuditBadge status={order.audit_status} reasons={order.audit_reasons} />
                                          {order.audit_required && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => setAuditPanelOrder(order)}
                                              className="h-5 px-1.5 text-[10px] font-medium text-[#4338CA] hover:text-[#3730A3] hover:bg-indigo-50"
                                              data-testid={`review-audit-${order.id}`}
                                            >
                                              {order.audit_status === 'completed' ? 'View' : 'Review'}
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                      {showInvoiceColumn && (
                                      <td className="px-3 py-2" data-testid={`order-invoice-cell-${order.id}`}>
                                        <span className="text-sm text-slate-500 font-mono">{order.invoice_number || '—'}</span>
                                      </td>
                                      )}
                                      <td className="px-3 py-2 text-center">
                                        {/* Jun 2026 — simplified row actions: Review /
                                            Refresh Price / Invoice removed. Product ID is
                                            now the review link, pricing auto-refreshes
                                            server-side, and per-order invoice PDFs live on
                                            the generated invoice. Only Exclude remains. */}
                                        {(!order.invoice_id && order.billing_status !== 'invoiced' && order.billing_status !== 'paid') ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setExcludeTarget(order); setExcludeReason(''); }}
                                            className="h-6 px-1.5 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50"
                                            data-testid={`row-exclude-billing-${order.id}`}
                                            title="Exclude this order from billing (test/void orders)"
                                          >
                                            Exclude
                                          </Button>
                                        ) : (
                                          <span className="text-xs text-slate-300">—</span>
                                        )}
                                      </td>
                                    </>
                                  )}
                                </>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  )}
                </div>
                
                {/* Pagination */}
                <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between" data-testid="orders-pagination">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-slate-500" data-testid="pagination-info">
                      {staleData ? 'Loading…' : (
                        <>Showing <span className="font-medium text-slate-700">{totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}</span>–<span className="font-medium text-slate-700">{Math.min(currentPage * pageSize, totalCount)}</span> of <span className="font-medium text-slate-700">{totalCount}</span> orders</>
                      )}
                    </p>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                      <span>Rows:</span>
                      {[10, 25, 50, 100].map(size => (
                        <button
                          key={size}
                          onClick={() => { setPageSize(size); setCurrentPage(1); }}
                          data-testid={`page-size-${size}`}
                          className={`bd-chip px-2.5 py-1 rounded-lg text-xs font-semibold ${
                            pageSize === size
                              ? 'bg-indigo-600 text-white shadow-[0_4px_10px_-4px_rgba(79,70,229,0.6)]'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {(() => {
                      const pages = [];
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPages, start + 4);
                      if (end - start < 4) start = Math.max(1, end - 4);
                      
                      if (start > 1) {
                        pages.push(
                          <Button key={1} variant="outline" size="sm" onClick={() => setCurrentPage(1)} className="h-8 w-8 p-0 text-xs">1</Button>
                        );
                        if (start > 2) pages.push(<span key="start-ellipsis" className="px-1 text-slate-400">...</span>);
                      }
                      
                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={i === currentPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className={`h-8 w-8 p-0 text-xs ${i === currentPage ? 'bg-slate-800 text-white hover:bg-slate-700' : ''}`}
                            data-testid={`page-${i}`}
                          >
                            {i}
                          </Button>
                        );
                      }
                      
                      if (end < totalPages) {
                        if (end < totalPages - 1) pages.push(<span key="end-ellipsis" className="px-1 text-slate-400">...</span>);
                        pages.push(
                          <Button key={totalPages} variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} className="h-8 w-8 p-0 text-xs">{totalPages}</Button>
                        );
                      }
                      
                      return pages;
                    })()}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                      data-testid="pagination-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Invoices Tab */}
            <TabsContent value="invoices" className="mt-0">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(15,23,42,0.05),0_12px_32px_-24px_rgba(15,23,42,0.3)] overflow-hidden" data-testid="invoices-table-card">
                {/* Compact section header (Apr 26, 2026)
                    — title + subtitle merged inline, matches the
                      Orders header pattern (px-6 py-2.5). Drops ~24px of
                      vertical real estate. Selection counter + "Mark
                      Selected as Paid" live in the slim contextual
                      toolbar above (only rendered when invoices are
                      selected, never reserves space when idle). */}
                <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                      Generated Invoices
                    </h3>
                    <span className="text-xs text-slate-500 truncate tabular-nums" data-testid="invoices-summary">
                      {staleData ? 'Loading…' : (
                        <>
                          {sortedInvoices.length} {sortedInvoices.length === 1 ? 'invoice' : 'invoices'}
                          {sortedInvoices.length > 0 && (
                            <> · {formatCurrency(sortedInvoices.reduce((s, inv) => s + (Number(inv.total_amount) || 0), 0))}</>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                
                {invoicesError && !isLoading && (
                  <PanelError message={`Invoices could not be loaded (${invoicesError}).`} onRetry={fetchBillingData} testid="invoices-panel-error" />
                )}
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bd-thead-navy border-b border-[#1E3A8A]">
                        <th className="px-3 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectedInvoiceIds.size > 0 && selectedInvoiceIds.size === sortedInvoices.filter(inv => inv.status === 'invoiced' || inv.status === 'partially_paid').length}
                            onChange={toggleSelectAllInvoices}
                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                            data-testid="select-all-invoices-checkbox"
                          />
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-left text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('invoice_number') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('invoice_number')}
                        >
                          <span className="flex items-center gap-1">
                            Invoice #
                            {getInvoicesSortDirection('invoice_number') && <span>{getInvoicesSortDirection('invoice_number') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-left text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('client_code') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('client_code')}
                        >
                          <span className="flex items-center gap-1">
                            Client
                            {getInvoicesSortDirection('client_code') && <span>{getInvoicesSortDirection('client_code') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-left text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('date_from') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('date_from')}
                        >
                          <span className="flex items-center gap-1">
                            Period
                            {getInvoicesSortDirection('date_from') && <span>{getInvoicesSortDirection('date_from') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-center text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('total_orders') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('total_orders')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Orders
                            {getInvoicesSortDirection('total_orders') && <span>{getInvoicesSortDirection('total_orders') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-right text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('total_amount') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('total_amount')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            Total
                            {getInvoicesSortDirection('total_amount') && <span>{getInvoicesSortDirection('total_amount') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th 
                          className={`px-4 py-[11px] text-center text-[12px] font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors ${getInvoicesSortDirection('status') ? 'bg-white/15 text-white' : ''}`}
                          onClick={() => handleInvoicesSort('status')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Status
                            {getInvoicesSortDirection('status') && <span>{getInvoicesSortDirection('status') === 'asc' ? '↑' : '↓'}</span>}
                          </span>
                        </th>
                        <th className="px-4 py-[11px] text-center text-[12px] font-semibold text-white uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody data-testid="invoices-tbody" data-stale={staleData ? 'true' : 'false'} aria-busy={staleData ? 'true' : 'false'}
                      className={`transition-opacity duration-200 ${invoicesHoldPrev ? 'opacity-40 pointer-events-none select-none' : 'opacity-100'}`}>
                      {(staleData && !invoicesHoldPrev) ? (
                        [0, 1, 2, 3].map((i) => (
                          <tr key={`inv-sk-${i}`} className="border-b border-slate-100" data-testid="invoices-skeleton-row">
                            {Array.from({ length: 8 }).map((_, j) => (
                              <td key={j} className="px-4 py-3.5"><div className="bd-skeleton h-3.5" style={{ width: `${55 + ((i * 7 + j * 13) % 40)}%` }} /></td>
                            ))}
                          </tr>
                        ))
                      ) : invoicesDisplayRows.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                                <Receipt className="w-6 h-6 text-slate-400" />
                              </div>
                              <p className="text-slate-500 font-medium">No invoices generated yet</p>
                              <p className="text-sm text-slate-400 mt-1">Click &quot;Generate Invoice&quot; to create one</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        invoicesDisplayRows.map((invoice, idx) => (
                          <tr
                            key={invoice.id}
                            className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                          >
                            <td className="px-3 py-4 w-10">
                              {invoice.status === 'invoiced' ? (
                                <input
                                  type="checkbox"
                                  checked={selectedInvoiceIds.has(invoice.id)}
                                  onChange={() => toggleInvoiceSelection(invoice.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                  data-testid={`select-invoice-${invoice.id}`}
                                />
                              ) : (
                                <span className="w-4 h-4 block" />
                              )}
                            </td>
                            <td className="px-6 py-3">
                              <span className="font-medium text-slate-800 font-mono">{invoice.invoice_number}</span>
                            </td>
                            <td className="px-6 py-3">
                              <span className="text-sm font-medium text-slate-600">{invoice.client_code}</span>
                              <span className="text-sm text-slate-400 ml-1">— {invoice.client_name}</span>
                            </td>
                            <td className="px-6 py-3">
                              <span className="text-sm text-slate-500">{formatDate(invoice.date_from)} — {formatDate(invoice.date_to)}</span>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <span className="text-sm font-medium text-slate-600">{invoice.total_orders}</span>
                            </td>
                            <td className="px-6 py-3 text-right">
                              <span className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(invoice.total_amount)}</span>
                            </td>
                            <td className="px-6 py-3 text-center">
                              <StatusBadge status={invoice.status} />
                            </td>
                            <td className="px-6 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => viewInvoiceDetails(invoice)}
                                      className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                      data-testid={`view-invoice-${invoice.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>View Details</TooltipContent>
                                </Tooltip>
                                
                                {/* INV-PDF-REMOVE-001 (Sep 2026): PDF download retired — Excel statement is the deliverable */}

                                {/* BILLING-INVOICE-XLSX-001 — snapshot Excel, any status */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadInvoiceXLSX(invoice.id, invoice.invoice_number)}
                                      className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                      data-testid={`download-xlsx-${invoice.id}`}
                                    >
                                      <FileSpreadsheet className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="font-semibold">Download Invoice Excel</div>
                                    <div className="text-[11px] opacity-80">Original Excel statement for this specific invoice</div>
                                  </TooltipContent>
                                </Tooltip>
                                
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEmailInvoice(invoice.id, invoice.invoice_number)}
                                      className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                      data-testid={`email-invoice-${invoice.id}`}
                                    >
                                      <Mail className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Email Invoice</TooltipContent>
                                </Tooltip>
                                
                                {invoice.status !== 'paid' && (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleRecordPayment(invoice)}
                                          className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                          data-testid={`record-payment-${invoice.id}`}
                                        >
                                          <Receipt className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Record Payment ($)</TooltipContent>
                                    </Tooltip>
                                    
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleMarkPaid(invoice)}
                                          className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                          data-testid={`mark-paid-${invoice.id}`}
                                        >
                                          <CreditCard className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {invoice.status === 'partially_paid' ? 'Mark Files Paid (line-item)' : 'Mark Files Paid'}
                                      </TooltipContent>
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          </>
          )}
        </div>
        
        {/* Invoice Preview Modal */}
        {showInvoicePreview && invoicePreviewData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="invoice-preview-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
              {/* Modal Header */}
              <div className="px-6 py-5 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Generate Invoice</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Period: {dateFrom} — {dateTo}
                </p>
              </div>
              
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Bill To completeness warning — Feb 25, 2026 */}
                {billToWarnings.length > 0 && (
                  <div
                    className="rounded-xl border border-amber-200 bg-amber-50 p-4"
                    data-testid="bill-to-warning-banner"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">
                          Bill To details incomplete for{' '}
                          {billToWarnings.length === 1
                            ? '1 client'
                            : `${billToWarnings.length} clients`}
                        </p>
                        <p className="text-xs text-amber-800 mt-1">
                          The invoice PDF will fall back to client name + code.
                          Update <strong>Customer Billing / Bill To</strong> on
                          the Client Code to fix this.
                        </p>
                        <ul className="mt-2 space-y-1">
                          {billToWarnings.map((w) => (
                            <li
                              key={w.client_code}
                              className="text-xs text-amber-900"
                              data-testid={`bill-to-warning-${w.client_code}`}
                            >
                              <span className="font-mono font-semibold">
                                {w.client_code}
                              </span>
                              {w.client_name && (
                                <span className="text-amber-700"> · {w.client_name}</span>
                              )}
                              <span className="text-amber-700">
                                {' — missing: '}
                                {w.missing_critical
                                  .map((f) => f.replace(/^billing_/, '').replace(/_/g, ' '))
                                  .join(', ')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Card */}
                <div className="bg-slate-50 rounded-xl p-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Orders</p>
                    <p className="text-2xl font-bold text-slate-900">{invoicePreviewData.count}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total Amount</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(invoicePreviewData.total)}</p>
                  </div>
                </div>
                
                {/* Client Breakdown */}
                {Object.keys(invoicePreviewData.byClient).length > 1 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">By Client ({Object.keys(invoicePreviewData.byClient).length} invoices will be created)</h4>
                    <div className="space-y-2">
                      {Object.entries(invoicePreviewData.byClient).map(([client, data]) => (
                        <div key={client} className="flex justify-between items-center px-3 py-2 bg-slate-50 rounded-lg text-sm">
                          <span className="font-medium text-slate-700">{client}</span>
                          <span className="text-slate-500">{data.orders.length} orders — <span className="font-semibold text-emerald-600">{formatCurrency(data.total)}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Compact Orders Table */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Orders</h4>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="tf-thead">
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Order #</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Product</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoicePreviewData.orders.map((o, i) => (
                          <tr key={o.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-3 py-2 font-mono text-slate-800">{o.order_number}</td>
                            <td className="px-3 py-2 text-slate-600">{o.client_product}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-600 tabular-nums">
                              {formatCurrency(o.total_billable_amount || o.billing_override_price || o.billing_unit_price_snapshot || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
                {invoiceGenSuccess && (
                  <div className="mr-auto flex items-center gap-2 text-emerald-700" data-testid="invoice-generated-success">
                    <span className="bd-success-pop w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="w-4 h-4" /></span>
                    <span className="text-sm font-semibold">
                      {invoiceGenSuccess.length === 1 ? `Invoice ${invoiceGenSuccess[0].invoice_number} created` : `${invoiceGenSuccess.length} invoices created`}
                    </span>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowInvoicePreview(false)}
                  disabled={isGeneratingInvoice}
                  data-testid="cancel-invoice-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmGenerateInvoice}
                  disabled={isGeneratingInvoice}
                  className={`bd-cta rounded-xl text-white shadow-lg ${invoiceGenSuccess ? 'bg-emerald-600 hover:bg-emerald-600 shadow-emerald-500/25' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-blue-500/25'}`}
                  data-testid="confirm-generate-invoice-btn"
                >
                  {invoiceGenSuccess ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Generated
                    </>
                  ) : isGeneratingInvoice ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirm & Generate
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Invoice Preview Modal (BILLING-BULK-001 — Feb 2026)
            Renders the dry-run response from
            POST /pricing/billing/invoices/generate-by-filter. Confirmation
            echoes back the preview_hash so backend can detect drift. */}
        {showBulkInvoicePreview && bulkPreviewData && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            data-testid="bulk-invoice-preview-modal"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
              <div className="px-6 py-5 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900" data-testid="bulk-preview-title">
                  {bulkPreviewData.outstanding ? 'Generate invoices for all outstanding orders?' : 'Invoice All Matching Orders'}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {bulkPreviewData.client_name || bulkPreviewData.client_code}
                  {' · '}
                  {bulkPreviewData.outstanding
                    ? `${bulkPreviewData.eligible_orders_count.toLocaleString()} orders · ${formatCurrency(bulkPreviewData.grand_total_amount)}`
                    : `${bulkPreviewData.date_from} — ${bulkPreviewData.date_to}`}
                </p>
                {bulkPreviewData.outstanding && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="bulk-preview-outstanding-note">
                    <div className="font-semibold tabular-nums" data-testid="bulk-preview-completion-range">
                      Completion range: {bulkPreviewData.completion_range?.from ? formatDisplayDate(bulkPreviewData.completion_range.from) : '—'}
                      {' – '}
                      {bulkPreviewData.completion_range?.to ? formatDisplayDate(bulkPreviewData.completion_range.to) : '—'}
                      {bulkPreviewData.search ? ` · search “${bulkPreviewData.search}”` : ''}
                    </div>
                    <div className="mt-0.5">This includes outstanding orders outside the currently displayed reporting date range.</div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Bill To completeness warning */}
                {bulkPreviewData.bill_to_completeness?.has_critical_gaps && (
                  <div
                    className="rounded-xl border border-red-200 bg-red-50 p-4"
                    data-testid="bulk-bill-to-warning"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-red-900">
                          Bill To details incomplete — generation will be blocked
                        </p>
                        <p className="text-xs text-red-800 mt-1">
                          Missing:&nbsp;
                          {(bulkPreviewData.bill_to_completeness.missing_critical || [])
                            .map((f) => f.replace(/^billing_/, '').replace(/_/g, ' '))
                            .join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-5 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Eligible Orders</p>
                    <p
                      className="text-2xl font-bold text-slate-900 tabular-nums"
                      data-testid="bulk-preview-eligible-count"
                    >
                      {bulkPreviewData.eligible_orders_count.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Invoices to Create</p>
                    <p
                      className="text-2xl font-bold text-indigo-600 tabular-nums"
                      data-testid="bulk-preview-invoices-count"
                    >
                      {bulkPreviewData.invoices_to_create}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      One consolidated invoice for all matching orders
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Grand Total</p>
                    <p
                      className="text-2xl font-bold text-emerald-600 tabular-nums"
                      data-testid="bulk-preview-grand-total"
                    >
                      {formatCurrency(bulkPreviewData.grand_total_amount)}
                    </p>
                  </div>
                </div>

                {/* Chunk breakdown — only when more than one chunk */}
                {bulkPreviewData.chunks.length > 1 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">
                      Invoice Chunks
                    </h4>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="tf-thead">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">#</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500">Range</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Orders</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkPreviewData.chunks.map((c) => (
                            <tr
                              key={c.chunk_index}
                              className={c.chunk_index % 2 === 1 ? 'bg-white' : 'bg-slate-50/50'}
                              data-testid={`bulk-preview-chunk-${c.chunk_index}`}
                            >
                              <td className="px-3 py-2 font-mono text-slate-700">{c.chunk_index}</td>
                              <td className="px-3 py-2 font-mono text-xs text-slate-600">
                                {c.first_order_number} → {c.last_order_number}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{c.orders_count}</td>
                              <td className="px-3 py-2 text-right font-semibold text-emerald-600 tabular-nums">
                                {formatCurrency(c.total_amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Exclusions */}
                {(() => {
                  const ex = bulkPreviewData.exclusions || {};
                  const rows = [
                    { key: 'delivery_not_completed', label: 'Delivery not completed', tone: 'amber' },
                    { key: 'missing_pricing', label: 'Missing pricing', tone: 'red' },
                    { key: 'already_invoiced', label: 'Already invoiced (drift)', tone: 'slate' },
                    { key: 'manual_deselected', label: 'Manually de-selected', tone: 'slate' },
                  ].filter((r) => (ex[r.key]?.count || 0) > 0);
                  if (!rows.length) return null;
                  return (
                    <div data-testid="bulk-preview-exclusions">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">
                        Excluded from this run
                      </h4>
                      <ul className="space-y-1.5">
                        {rows.map((r) => (
                          <li
                            key={r.key}
                            className={`px-3 py-2 rounded-lg text-xs flex items-start justify-between gap-3 ${
                              r.tone === 'red' ? 'bg-red-50 text-red-800'
                              : r.tone === 'amber' ? 'bg-amber-50 text-amber-800'
                              : 'bg-slate-50 text-slate-700'
                            }`}
                            data-testid={`bulk-preview-exclusion-${r.key}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold">{r.label} — {ex[r.key].count}</p>
                              {ex[r.key].order_numbers?.length > 0 && (
                                <p className="font-mono text-[10px] mt-0.5 break-all">
                                  {ex[r.key].order_numbers.join(', ')}
                                  {ex[r.key].truncated && ' …'}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                <p className="text-[11px] text-slate-400 font-mono break-all">
                  preview_hash: {bulkPreviewData.preview_hash.slice(0, 16)}…
                </p>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkInvoicePreview(false);
                    setBulkPreviewData(null);
                  }}
                  disabled={isGeneratingBulkInvoices}
                  data-testid="cancel-bulk-invoice-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmBulkGenerate}
                  disabled={
                    isGeneratingBulkInvoices
                    || bulkPreviewData.eligible_orders_count === 0
                    || bulkPreviewData.bill_to_completeness?.has_critical_gaps
                  }
                  className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-lg shadow-indigo-500/25"
                  data-testid="confirm-bulk-generate-btn"
                >
                  {isGeneratingBulkInvoices ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating {bulkPreviewData.invoices_to_create} Invoice{bulkPreviewData.invoices_to_create === 1 ? '' : 's'}…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Generate {bulkPreviewData.invoices_to_create} Invoice{bulkPreviewData.invoices_to_create === 1 ? '' : 's'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Details Dialog */}
        {emailInvoiceTarget && (
          <SendInvoiceModal invoice={emailInvoiceTarget} onClose={() => setEmailInvoiceTarget(null)} onSent={() => fetchBillingData()} />
        )}
        <InvoiceDetailsDialog
          open={showInvoiceDetailsDialog}
          onOpenChange={setShowInvoiceDetailsDialog}
          invoice={selectedInvoice}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onDownloadXLSX={handleDownloadInvoiceXLSX}
          onEmailInvoice={handleEmailInvoice}
          onMarkPaid={handleMarkPaid}
        />
        
        {/* Partial Payment Modal (Apr 26, 2026) */}
        <InvoicePaymentModal
          open={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          invoice={paymentModalInvoice}
          onPaymentApplied={handlePaymentApplied}
        />
        
        {/* Phase C.2 — Record Payment by dollar-amount modal */}
        <RecordPaymentModal
          open={showRecordPaymentModal}
          onOpenChange={setShowRecordPaymentModal}
          invoice={recordPaymentInvoice}
          onPaymentApplied={handleRecordPaymentApplied}
        />

        {/* BULK-PAY-MODAL-001 — compact Bulk Payment Upload modal (upload → match → apply) */}
        <BulkPaymentUploadModal
          open={showBulkPaymentUpload}
          onOpenChange={setShowBulkPaymentUpload}
          onApplied={() => { fetchBillingData(); }}
        />
        
        {/* Bulk Mark Invoices Paid Modal */}
        {showBulkPayConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="bulk-pay-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
              <div className="px-6 py-5 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Mark Invoices as Paid</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedInvoiceIds.size} invoice(s) — {formatCurrency(selectedInvoicesTotal)}
                </p>
              </div>
              
              <div className="px-6 py-5 space-y-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Invoices to mark paid:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedInvoices.map(inv => (
                      <div key={inv.id} className="flex justify-between text-sm">
                        <span className="font-mono text-slate-700">{inv.invoice_number}</span>
                        <span className="text-emerald-600 font-medium">{formatCurrency(inv.total_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Reference</label>
                  <input
                    type="text"
                    value={bulkPaymentRef}
                    onChange={(e) => setBulkPaymentRef(e.target.value)}
                    placeholder="Check #, wire ref, etc."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="bulk-pay-reference-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={bulkPaymentDate}
                    onChange={(e) => setBulkPaymentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="bulk-pay-date-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={bulkPaymentNotes}
                    onChange={(e) => setBulkPaymentNotes(e.target.value)}
                    placeholder="Any additional notes..."
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    data-testid="bulk-pay-notes-input"
                  />
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkPayConfirm(false)}
                  disabled={isBulkPaying}
                  data-testid="cancel-bulk-pay-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkMarkInvoicesPaid}
                  disabled={isBulkPaying}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg shadow-emerald-500/25"
                  data-testid="confirm-bulk-pay-btn"
                >
                  {isBulkPaying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirm Payment
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Audit Review Panel */}
        {auditPanelOrder && (
          <AuditReviewPanel
            order={auditPanelOrder}
            onClose={() => setAuditPanelOrder(null)}
            onOrderUpdated={() => fetchBillingData()}
            navigate={navigate}
          />
        )}

        {/* Jul 2026 — BILLING-EXCLUDE-001: exclude-from-billing confirmation */}
        {excludeTarget && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="exclude-billing-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-base font-semibold text-slate-900">Exclude Order from Billing</h3>
                <p className="text-sm text-slate-500 mt-1">
                  <span className="font-medium text-slate-700">{excludeTarget.order_number || excludeTarget.product_id}</span>
                  {' '}will be removed from the billing queue, invoice generation, exports and totals.
                  The order itself is NOT deleted and remains available for audit — this action is reversible.
                </p>
              </div>
              <div className="px-6 py-4 space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={excludeReason}
                  onChange={(e) => setExcludeReason(e.target.value)}
                  placeholder="e.g. Test order created during UAT — must never be invoiced"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                  data-testid="exclude-billing-reason-input"
                />
                <p className="text-xs text-slate-400">Recorded in the audit log (who, when, why). Minimum 5 characters.</p>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setExcludeTarget(null); setExcludeReason(''); }}
                  disabled={isExcluding}
                  data-testid="cancel-exclude-billing-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmExclude}
                  disabled={isExcluding || excludeReason.trim().length < 5}
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg shadow-red-500/25"
                  data-testid="confirm-exclude-billing-btn"
                >
                  {isExcluding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Excluding...
                    </>
                  ) : (
                    'Exclude from Billing'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default BillingDashboard;
